import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Container,
  Grid,
  Typography,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  Radio,
  styled,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import moment from "moment";
import { attentionColors, mobiliTheme } from "../../../../../themes/mobiliTheme";
import { getKvnrError } from "../../../../../utils/kvnrValidator";
import { resolveSourcedValue } from "../rxFieldSource";
import { Add, Check, CheckBox, Delete, PersonSearch } from "@mui/icons-material";
import Controls from "../../../../../atoms/Controls";
import TextArea from "../../../../../atoms/TextArea";
import { observer } from "mobx-react";
import { initialValues } from "../../../../pages/Patient/initialValues";
import useStore from "../../../../../helpers/useStore";
import {
  useMasterTables,
  resolveTarifGroupForRxType,
  useIcdCodes,
} from "../../../../../stores/MasterTables";
import { useCatalogTerms } from "../../../../../stores/MasterTables/therapies.queries";
import { DoctorContext } from "../../../../../stores/Doctor/doctor.provider";
import { RxStoreContext } from "../../../../../stores/Rx/rx.provider";
import { NoteDto, RxDto, RxDtoProcessingStatusEnum, TherapyRxDto, PrescribedTherapyDto } from "../../../../../api-client";
import { v4 as uuidv4 } from "uuid";
import { diffTherapyLines, existingTherapyLinesOf } from "./therapyLineDiff";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { confirmDoctorClinicJoin } from "../../../../DoctorManagement/confirmDoctorClinicJoin";
import { confirmDoctorClinicMismatch } from "../../../../DoctorManagement/confirmDoctorClinicMismatch";
import { UserContext } from "../../../../../stores/User/User.provider";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import DoctorDialog from "../../../../PatientDataEntryForm/Doctor";
import { PatientsContext } from "../../../../../stores/Patients/patients.provider";
import ValidationCardRxInfoPage from "../../../../../molecules/ValidationCardRxInfoPage";
import FlatCard from "../../../../../atoms/FlatCard";
import classes from "./Zahnarzt.module.css";
import ValidationCard, { formType } from "../../../../../molecules/ValidationCard";
import WarningDialog from "../../../../../atoms/WarningDialog";
import debounce from "lodash.debounce";
import ValidationTooltip from "../../../../../atoms/validationToolTip";
import DoctorCard from "../../../../../molecules/DoctorCard";
import { getStatusColor } from "../statusColor";
import { NotesContext } from "../../../../../stores/Notes/notes.provider";
import Notes from "../../../../ScheduleOverview/Notes/Notes";

import { rxuseForm } from "../../../../PatientDataEntryForm/Form";
import {
  ClinicForModal,
  Doctor,
} from "../../../../DoctorManagement/DoctorTabContent/doctor-client-types";
import VerordnungDialog from "../Kasse/VerordnungDialog";
import SimpleDocumentViewer from "../../../../Documents/SimpleDocumentViewer";
import { useRx } from "../../../../../stores/Rx/rx.queries";
import { usePatient } from "../../../../../stores/Patients/patient.queries";
import { useSaveRxs } from "../../../../../stores/Rx/rx.mutations";
import { EntryType } from "../../../../PatientDataEntryForm/Receipt";
import { formTherapyFromLine } from "../../../../../helpers/therapyRxLabel";
import { RxFormRow, rowNamesTerm, rowTermText } from "../../../../../helpers/rxFormRow";
import TherapyTermField from "../../../../../molecules/TherapyTermField";
import { usePrescribedTherapyOptions } from "../../../../../stores/MasterTables/masterTables.queries";

interface DiagnoseGroup {
  a: string;
  b: string;
  c: string;
  d: string;
}

interface ZahnarztProps {
  selectedRxId?: string;
  patientId?: number;
  handleNewFormData?: (data: RxDto) => void;
  newFormData?: any;
  values?: any; //required only entryType is newRx
  readOnlyView?: boolean;
  entryType?: EntryType;
  openRx?: boolean;
  onCloseRx?: any;
  onRxSave?: (rxId: string) => void;
  updateRxProcessingStatus?: (event: any) => void;
}

const ScalableDiv = styled("div")<{ occupiedHeight?: number }>(({ theme, occupiedHeight = 1 }) => ({
  transformOrigin: "top left",
  [theme.breakpoints.down("sm")]: {
    transform: `scale(${0.6 * occupiedHeight})`,
  },
  [theme.breakpoints.between("sm", "xl")]: {
    transform: `scale(${0.65 * occupiedHeight})`,
  },
  [theme.breakpoints.up("xl")]: {
    transform: `scale(${0.85 * occupiedHeight})`,
  },
}));

const ScalableComponentSimple = ({ children, occupiedHeight = 1 }: { children: React.ReactNode; occupiedHeight?: number }) => {
  return <ScalableDiv occupiedHeight={occupiedHeight}>{children}</ScalableDiv>;
};

const Zahnarzt: React.FC<ZahnarztProps> = observer(
  ({
    selectedRxId,
    patientId,
    handleNewFormData,
    newFormData,
    values,
    readOnlyView,
    entryType,
    onCloseRx,
    openRx,
    onRxSave,
    updateRxProcessingStatus,
  }) => {
    const { data: masterTablesDataQuery } = useMasterTables();
    // Prescribed TERMS — the catalog level a prescription names (MD-232).
    const { data: termOptions = [] } = usePrescribedTherapyOptions();
    // Fetch RX data using the rxId from store
    const { data: rxData, isLoading: isRxLoading, error: rxError } = useRx(selectedRxId!);
    const rxFormData = entryType && newFormData ? newFormData : rxData;
    // Get patient data from store
    const { data: patientData } = usePatient(patientId!);
    const patientValues = entryType === EntryType.newRx && values ? values : patientData;
    const { mutate: patientSaveRxs } = useSaveRxs();
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const safeMasterTablesData = masterTablesDataQuery || [];
    const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);
    const UserStore = useStore(UserContext);
    const accounts = useUserAccounts();
    const patientStore = useStore(PatientsContext);
    const NotesStore = useStore(NotesContext);
    const { getRxNotes, updateNoteById, postNotes, fetchNotes, setFetchNotes } = NotesStore;
    const [currentICD, setCurrentICD] = useState(null);
    const { data: icdObject } = useIcdCodes(currentICD, false); // disabled initially
 
    const { getLiveValidation } = patientStore;
    const rxStore = useStore(RxStoreContext);
    const { savedSeriesForPatient } = rxStore;
    const { data: catalogTerms } = useCatalogTerms();

    const { user } = UserStore;
    // TODO: Replace getICD with appropriate hook-based solution
    const [selectedDiagnoseGroup, setSelectedDiagnoseGroup] = useState<DiagnoseGroup | null>(null);
    const RxStore = useStore(RxStoreContext);
    const { saveRxs } = RxStore;
    const [loading, setLoading] = useState(false);
    const [rxNotes, setRxNotes] = useState<NoteDto[]>([]);

    //For payer
    const insurer = safeMasterTablesData
      .filter((obj: any) => "insurer" in obj)
      .map((obj: any) => obj.insurer);
    const [selectedPayer, setSelectedPayer] = useState(null);
    const [selectedChipCard, setSelectedChipCard] = useState<string | null>(null);
    const diagnoseGroups = safeMasterTablesData
      .filter((obj: any) => "diagnosisGroups" in obj)
      .map((obj: any) => obj.diagnosisGroups);

    // const diagnoseGroups = masterTablesData
    // .filter((obj: any) => obj.RxType?.id === "Zahnarzt") // Filter objects where RxType.id is "Zahnarzt"
    // .map((obj: any) => obj.diagnosisGroups); // Map to extract the diagnosisGroups

    const therapyOptions = safeMasterTablesData
      .filter((obj: any) => "therapy" in obj)
      .map((obj: any) => obj.therapy);
         // console.log("ZSZSHDHDHHDHDH", therapyOptions, therapyOptions[0],safeMasterTablesData);

    const rxType = safeMasterTablesData
      .filter((obj: any) => "rxType" in obj) // Filter objects that have the insuranceStatus property
      .map((obj: any) => obj.rxType);
    //  therapyOptions= therapyOptions[0].filter(
    //       (therapyObj: any) =>

    //         therapyObj.duration
    //     )

    //For Doctor
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const DoctorStore = useStore(DoctorContext);
    const { getClinicById, getDoctorById, createDoctorWithClinic } = DoctorStore;
    const [isSelectActive, setIsSelectActive] = useState(false);
    const [isAddEditDialogOpen, setAddEditDialogOpen] = useState(false);
    // const [isDoctorNumberDisabled, setIsDoctorNumberDisabled] = useState(true);
    const [doesClinicExist, setDoesClinicExist] = useState(false);
    const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
    const [isClinicModalEditMode, setIsClinicModalEditMode] = useState(false);
    const [currentClinicId, setCurrentClinicId] = useState("");
    const [creationResponse, setCreationResponse] = useState<any>({
      id: "",
      clinics: [],
      contactInfo: {},
      doctorId: "",
      clinicId: "",
    });
    const [currentDoctorId, setCurrentDoctorId] = useState("");
    const [doesDoctorExist, setDoesDoctorExist] = useState(false);
    const [currentClinic, setCurrentClinic] = useState<ClinicForModal | undefined>();
    const [showClinicData, setShowClinicData] = useState(false);
    const [currentDoctor, setCurrentDoctor] = useState<Doctor | ClinicForModal>();
    const [autocompleteSearchTerm, setAutocompleteSearchTerm] = useState("");

    //  Track selected therapy terms from checkboxes
    const [selectedTherapyTerms, setSelectedTherapyTerms] = useState<Set<string>>(new Set());
    const [doubledTherapyTerms, setDoubledTherapyTerms] = useState<Set<string>>(new Set());
    const [termRxSeriesMap, setTermRxSeriesMap] = useState<Record<string, { id?: string; label?: string }>>({});

    // Main therapy frequency (1x, 2x, 3x) and amount (Verordnungsmenge)
    const [mainTherapyFrequency, setMainTherapyFrequency] = useState<string>("");
    const [mainTherapyAmount, setMainTherapyAmount] = useState<string>("");

    //Supplementary therapy (ergänzendes Heilmittel) frequency and amount
    const [suppTherapyFrequency, setSuppTherapyFrequency] = useState<string>("");
    const [suppTherapyAmount, setSuppTherapyAmount] = useState<string>("");

    // The paper form lets the doctor tick several frequency boxes to prescribe a
    // range ("1-3x", "2-3x"), and the therapy_frequency catalog already carries
    // those rows ("1", "2", "3", "1-2", "1-3", "2-3", ...) with id === text. The
    // state holds that catalog text; these helpers translate between it and the
    // ticked boxes.
    const parseFrequencyValues = (frequency: string): Set<number> => {
      const match = /^(\d+)(?:\s*-\s*(\d+))?$/.exec((frequency || "").trim());
      if (!match) return new Set<number>();
      const min = Number(match[1]);
      const max = match[2] ? Number(match[2]) : min;
      const values = new Set<number>();
      for (let value = min; value <= max; value++) {
        values.add(value);
      }
      return values;
    };

    const toFrequencyText = (values: Set<number>): string => {
      if (values.size === 0) return "";
      const sorted = Array.from(values).sort((a, b) => a - b);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      return min === max ? `${min}` : `${min}-${max}`;
    };

    const toggleFrequencyValue = (
      current: string,
      value: number,
      checked: boolean
    ): string => {
      const values = parseFrequencyValues(current);
      if (checked) {
        values.add(value);
      } else {
        values.delete(value);
      }
      return toFrequencyText(values);
    };

    // Define which therapy terms are supplementary (ergänzende Heilmittel)
    const CHECKBOX_THERAPY_TERMS = [
      "KG",
      "KG-ZNS-Kinder",
      "KG-ZNS-Kinder Bobath",
      "KG-ZNS-Kinder Vojta",
      "KG-ZNS",
      "KG-ZNS Bobath",
      "KG-ZNS Vojta",
      "KG-ZNS PNF",
      "MT",
      "MLD 30",
      "MLD 45",
      "Kälte",
      "Wärme",
      "Heißluft",
      "Heiße Rolle",
      "Ultraschall",
      "Packungen",
      "Übungsbehandlung",
      "Elektrostimulation",
      "Elektrotherapie"
    ];
    const SUPPLEMENTARY_THERAPIES = new Set([
      "Kälte", "Wärme", "Heißluft", "Heiße Rolle", "Ultraschall", "Packungen", "Übungsbehandlung","Elektrostimulation", "Elektrotherapie"
    ]);

   // Parent-child therapy groups - only ONE child allowed per parent (radio behavior)
    // Parent can be selected alone (means practice chooses the specific therapy)
    const THERAPY_GROUPS: Record<string, string[]> = {
      "Wärme": ["Heißluft", "Heiße Rolle", "Ultraschall", "Packungen"],
      "KG-ZNS-Kinder": ["KG-ZNS-Kinder Bobath", "KG-ZNS-Kinder Vojta"],
      "KG-ZNS": ["KG-ZNS Bobath", "KG-ZNS Vojta", "KG-ZNS PNF"],
    };

    // // Groups whose children may be prescribed together. The Wärme children are
    // // separate supplementary remedies (a prescription can carry Heißluft AND
    // // Packungen), whereas the KG-ZNS variants are mutually exclusive techniques
    // // and keep the one-of radio behaviour.
    // const MULTI_SELECT_GROUPS = ["Wärme"];
    // const allowsMultipleChildren = (parentTerm: string) =>
    //   MULTI_SELECT_GROUPS.some(
    //     (parent) => normalizeTherapyTerm(parent) === normalizeTherapyTerm(parentTerm)
    //   );

    // Get the parent category for a child term (or null if not a child)
    const getParentForChild = (childTerm: string): string | null => {
      const normalizedChild = normalizeTherapyTerm(childTerm);
      for (const [parent, children] of Object.entries(THERAPY_GROUPS)) {
        if (children.some(c => normalizeTherapyTerm(c) === normalizedChild)) {
          return parent;
        }
      }
      return null;
    };

    // Check if term is a parent category
    const isParentCategory = (term: string): boolean => 
      Object.keys(THERAPY_GROUPS).some(p => normalizeTherapyTerm(p) === normalizeTherapyTerm(term));

    // Get children of a parent (or empty array)
    const getChildrenOfParent = (parentTerm: string): string[] => {
      for (const [parent, children] of Object.entries(THERAPY_GROUPS)) {
        if (normalizeTherapyTerm(parent) === normalizeTherapyTerm(parentTerm)) {
          return children;
        }
      }
      return [];
    };

    const normalizeTherapyTerm = (term: string) => term.trim().replace(/[_\s]+/g, "-").toUpperCase();
    const resolveTherapyTermLabel = (termLabel: string) => {
      const directMatch = CHECKBOX_THERAPY_TERMS.find(
        (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(termLabel)
      );
      if (directMatch) {
        return directMatch;
      }

      const catalogMatch = (catalogTerms || []).find((catalogTerm: any) => {
        const aliasTexts = (catalogTerm?.aliases || []).map((alias: any) =>
          typeof alias === "string" ? alias : alias?.alias
        );
        const candidates = [catalogTerm?.label, catalogTerm?.id, ...aliasTexts].filter(Boolean);
        return candidates.some(
          (candidate: string) => normalizeTherapyTerm(candidate) === normalizeTherapyTerm(termLabel)
        );
      });

      if (catalogMatch) {
        const aliasTexts = (catalogMatch?.aliases || []).map((alias: any) =>
          typeof alias === "string" ? alias : alias?.alias
        );
        const mapCandidates = [
          catalogMatch?.label,
          catalogMatch?.id,
          ...aliasTexts,
        ].filter(Boolean);

        const mappedCheckbox = CHECKBOX_THERAPY_TERMS.find(
          (term) =>
            mapCandidates.some(
              (candidate: string) =>
                normalizeTherapyTerm(term) === normalizeTherapyTerm(candidate)
            )
        );
        if (mappedCheckbox) {
          return mappedCheckbox;
        }
      }

      return termLabel;
    };

    /**
     * SUPPLEMENTARY_THERAPIES holds the nine CHECKBOX spellings, so it only
     * answers for text that is already one of them. `therapyLineDiff` asks this
     * about SAVED labels too, where a term arrives under any of its aliases
     * ("PA" for Warmpackung, "HR" for Heiße Rolle, "Fango", "Eis"), and every
     * one of those read as a main Heilmittel — which is what stopped a
     * supplementary line from being recognised as its own. The catalog knows
     * the real answer; fall back to it before giving up.
     */
    const isSupplementaryTherapy = (termLabel: string) => {
      if (
        Array.from(SUPPLEMENTARY_THERAPIES).some(
          (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(termLabel)
        )
      ) {
        return true;
      }
      const normalized = normalizeTherapyTerm(termLabel);
      const catalogMatch = (termOptions || []).find((option: any) =>
        [option?.id, option?.label, option?.shortLabel, ...(option?.aliases ?? [])]
          .filter(Boolean)
          .some((candidate: string) => normalizeTherapyTerm(candidate) === normalized)
      );
      return !!catalogMatch?.isSupplementary;
    };

    // Handler for therapy checkbox clicks - adds/removes therapy terms
    // Implements radio behavior for children under parent categories
    const handleTherapyCheckbox = (termLabel: string, checked: boolean) => {
      setUnsavedChanges(true);
      const canonicalTerm = resolveTherapyTermLabel(termLabel);
      const parent = getParentForChild(canonicalTerm);

      setSelectedTherapyTerms((prev) => {
        const next = new Set(prev);

        if (checked) {
          // If this is a child term, implement radio behavior
          if (parent) {
            // Remove any other children of the same parent (radio behavior - only one child allowed)
            const siblings = getChildrenOfParent(parent);
            siblings.forEach(sibling => {
              Array.from(next).forEach(term => {
                if (normalizeTherapyTerm(term) === normalizeTherapyTerm(sibling)) {
                  next.delete(term);
                }
              });
            });
            // Auto-select the parent when a child is selected
            if (!Array.from(next).some(t => normalizeTherapyTerm(t) === normalizeTherapyTerm(parent))) {
              next.add(parent);
            }
          }
          // Add the selected term
          next.add(canonicalTerm);
        } else {
          // Removing a term
          Array.from(next).forEach(term => {
            if (normalizeTherapyTerm(term) === normalizeTherapyTerm(canonicalTerm)) {
              next.delete(term);
            }
          });

        // if (checked) {
        //   // Mutually exclusive groups keep radio behaviour: selecting a child
        //   // clears its siblings. Multi-select groups (Wärme) leave them alone.
        //   if (parent && !allowsMultipleChildren(parent)) {
        //     const siblings = getChildrenOfParent(parent);
        //     siblings.forEach(sibling => {
        //       Array.from(next).forEach(term => {
        //         if (normalizeTherapyTerm(term) === normalizeTherapyTerm(sibling)) {
        //           next.delete(term);
        //         }
        //       });
        //     });
        //   }
        //   // The parent is deliberately NOT auto-selected: it is its own
        //   // prescription position ("practice chooses"), and adding it alongside
        //   // a child put an extra "Wärme" line on the Rx.
        //   next.add(canonicalTerm);
        // } else {
        //   // Removing a term
        //   Array.from(next).forEach(term => {
        //     if (normalizeTherapyTerm(term) === normalizeTherapyTerm(canonicalTerm)) {
        //       next.delete(term);
        //     }
        //   });
          
          // If unchecking a parent, also remove all its children
          if (isParentCategory(canonicalTerm)) {
            const children = getChildrenOfParent(canonicalTerm);
            children.forEach(child => {
              Array.from(next).forEach(term => {
                if (normalizeTherapyTerm(term) === normalizeTherapyTerm(child)) {
                  next.delete(term);
                }
              });
            });
          }
        }

        return next;
      });

      // Clean up doubled terms and series map when unchecking
      if (!checked) {
        setDoubledTherapyTerms((prev) =>
          new Set(
            Array.from(prev).filter(
              (term) => normalizeTherapyTerm(term) !== normalizeTherapyTerm(canonicalTerm)
            )
          )
        );
        setTermRxSeriesMap((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            if (normalizeTherapyTerm(key) === normalizeTherapyTerm(canonicalTerm)) {
              delete next[key];
            }
          });
          return next;
        });
      }
    };

    // Check if a therapy term is selected
    const isTherapySelected = (termLabel: string) => {
      // For parent categories, check if parent itself OR any child is selected
      if (isParentCategory(termLabel)) {
        const parentSelected = Array.from(selectedTherapyTerms).some(
          (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(termLabel)
        );
        const children = getChildrenOfParent(termLabel);
        const childSelected = children.some(child =>
          Array.from(selectedTherapyTerms).some(
            (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(child)
          )
        );
        return parentSelected || childSelected;
      }
      return Array.from(selectedTherapyTerms).some(
        (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(termLabel)
      );
    };

    const isTherapyDoubled = (termLabel: string) =>
      Array.from(doubledTherapyTerms).some(
        (term) => normalizeTherapyTerm(term) === normalizeTherapyTerm(termLabel)
      );

    const handleDoubledTherapyCheckbox = (termLabel: string, checked: boolean) => {
      const canonicalTerm = resolveTherapyTermLabel(termLabel);
      setUnsavedChanges(true);
      setDoubledTherapyTerms((prev) => {
        const next = new Set(
          Array.from(prev).filter(
            (term) => normalizeTherapyTerm(term) !== normalizeTherapyTerm(canonicalTerm)
          )
        );
        if (checked) {
          next.add(canonicalTerm);
        }
        return next;
      });
    };

    const getTherapyRxSeries = (termLabel: string) => {
      const matchKey = Object.keys(termRxSeriesMap).find(
        (key) => normalizeTherapyTerm(key) === normalizeTherapyTerm(termLabel)
      );
      return matchKey ? termRxSeriesMap[matchKey] : undefined;
    };

    const handleTermRxSeriesChange = (termLabel: string, event: any) => {
      const canonicalTerm = resolveTherapyTermLabel(termLabel);
      const seriesId = event?.target?.id || "";
      const seriesLabel = event?.target?.value || "";
      setUnsavedChanges(true);
      setTermRxSeriesMap((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (normalizeTherapyTerm(key) === normalizeTherapyTerm(canonicalTerm)) {
            delete next[key];
          }
        });
        if (seriesId || seriesLabel) {
          next[canonicalTerm] = { id: seriesId || undefined, label: seriesLabel || undefined };
        }
        return next;
      });
    };

    const selectedTherapyTermsForTable = useMemo(() => {
      // For parent-child groups: show child if selected, otherwise show parent if selected alone
      // Don't show both parent and child together in the table
      const termsToShow: string[] = [];
      const processedParents = new Set<string>();

      // First pass: handle parent-child relationships
      for (const [parent, children] of Object.entries(THERAPY_GROUPS)) {
        const parentSelected = Array.from(selectedTherapyTerms).some(
          t => normalizeTherapyTerm(t) === normalizeTherapyTerm(parent)
        );
        const selectedChild = children.find(child =>
          Array.from(selectedTherapyTerms).some(
            t => normalizeTherapyTerm(t) === normalizeTherapyTerm(child)
          )
        );

        if (selectedChild) {
          // Child selected - show only child, not parent
          termsToShow.push(selectedChild);
        } else if (parentSelected) {
          // Only parent selected (no specific child) - show parent
          termsToShow.push(parent);
        }
        processedParents.add(normalizeTherapyTerm(parent));
        children.forEach(c => processedParents.add(normalizeTherapyTerm(c)));
      }

      // Second pass: add terms that are not part of any parent-child group
      const ordered = CHECKBOX_THERAPY_TERMS.filter(
        (term) => 
          isTherapySelected(term) && 
          !processedParents.has(normalizeTherapyTerm(term))
      );
      
      const extras = Array.from(selectedTherapyTerms).filter(
        (term) =>
          !processedParents.has(normalizeTherapyTerm(term)) &&
          !CHECKBOX_THERAPY_TERMS.some(
            (known) => normalizeTherapyTerm(known) === normalizeTherapyTerm(term)
          )
      );

      return [...termsToShow, ...ordered, ...extras];
    }, [selectedTherapyTerms]);

    const applyLoadedTherapyUiState = ({
      terms,
      doubledTerms,
      seriesByTerm,
      mainFreq,
      mainAmt,
      suppFreq,
      suppAmt,
    }: {
      terms: Set<string>;
      doubledTerms: Set<string>;
      seriesByTerm: Record<string, { id?: string; label?: string }>;
      mainFreq: string;
      mainAmt: string;
      suppFreq: string;
      suppAmt: string;
    }) => {
      setSelectedTherapyTerms(terms);
      setDoubledTherapyTerms(doubledTerms);
      setTermRxSeriesMap(seriesByTerm);
      setMainTherapyFrequency(mainFreq);
      setMainTherapyAmount(mainAmt);
      setSuppTherapyFrequency(suppFreq);
      setSuppTherapyAmount(suppAmt);
    };

    useEffect(() => {
      setCurrentDoctor(rxFormData?.doctor as any);
      setCurrentClinic(rxFormData?.clinic as any);
    }, [rxFormData?.doctor, rxFormData?.clinic]);

    // Keep display fields in sync with the actual doctor/clinic LANR/BSNR
    useEffect(() => {
      if (currentDoctor) {
        const lanr = (currentDoctor as any).LANR || "";
        setFormData((prev) => ({ ...prev, doctorNumber: lanr, doctorId: (currentDoctor as any).id || prev.doctorId }));
      }
    }, [currentDoctor]);

    useEffect(() => {
      if (currentClinic) {
        const bsnr = (currentClinic as any).BSNR || "";
        setFormData((prev) => ({ ...prev, practiceNumber: bsnr, clinicId: currentClinic.id || prev.clinicId }));
      }
    }, [currentClinic]);

    useEffect(() => {
      if (creationResponse && creationResponse.doctorId && creationResponse.clinicId) {
        setCurrentDoctor({
          id: creationResponse.doctorId,
          LANR: creationResponse.LANR,
          contactInfo: creationResponse.contactInfo as any,
          clinics: creationResponse.clinics,
        });
        const matchedClinic = (creationResponse.clinics || []).find((clinic: any) => clinic.id === creationResponse.clinicId);
        setCurrentClinic(matchedClinic as any || undefined);
        setFormData((prev) => ({
          ...prev,
          doctorId: creationResponse.doctorId || "",
          clinicId: creationResponse.clinicId || "",
          doctorNumber: creationResponse.LANR || "",
          practiceNumber: matchedClinic?.BSNR || "",
        }));
      }
    }, [creationResponse]);

    const account = safeMasterTablesData
      .filter((obj: any) => "account" in obj)
      .map((obj: any) => obj.account);

    /**
     * "" is what an untouched form field holds; null is what a nullable column
     * accepts. Only the empty string is converted — false, 0 and a real value
     * all pass through, so an explicitly unticked box stays unticked.
     */
    const emptyToNull = <T,>(value: T): T | null =>
      (value as unknown) === "" ? null : value;

    const initialrxValues = {
      Receiptpayer: patientValues?.payer,
      address: "",
      processingStatus: RxDtoProcessingStatusEnum.InBehandlung,
      status: "",
      signatureStatus: "",
      insuranceStatus: "",
      chipcard: "",
      tarifGroupId: "",
      insureeNumber: "",
      rxtype: "Kasse",
      accountId: "",

      isPerpetual: false,

      practiceNumber: "",
      doctorNumber: "",
      doctorId: "",
      clinicId: "",
      date: "",
      isCopaymentExempted: false,
      isAccident: false,
      isBVG: false,
      icdCodeEntries: [
        { id: "", userText: "" },
        { id: "", userText: "" },
      ],
      diagnosisText: "",
      diagnosegroup: null,
      a: "",
      b: "",
      c: "",
      d: "",
      hasIndividualSymptoms: false,
      therapyRxs: [
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "" },
          rawText: null,
          prescribedTherapyId: null,
          prescribedTherapy: null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "" },
          rawText: null,
          prescribedTherapyId: null,
          prescribedTherapy: null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "" },
          rawText: null,
          prescribedTherapyId: null,
          prescribedTherapy: null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
      ],
      isHomeVisit: rxFormData?.isHomeVisit ?? null,
      therapyReport: false,
      urgent: false,
      symptoms: "",
      therapyFrequency: "",
      amount: "",
      additionalNotes: "",
      additionalNotes2: "",
      duplicate: false,
      clinic: {} as any,
    };

    const [formData, setFormData] = useState(initialrxValues);


    // Patient-only fields: never live on the Rx entity, so read them straight
    // from the patient record (the single source of truth).
    const patientLastName = patientValues?.lastName || "";
    const patientFirstName = patientValues?.firstName || "";
    const patientDob = patientValues?.dob ? moment(patientValues.dob).format("YYYY-MM-DD") : "";

    // Dual-sourced fields (saved on both the Rx and the patient record). formData
    // holds the Rx-native value; the resolver applies the patient fallback for
    // display while exposing `.source` ("rx" | "patient" | "none") so the UI can
    // make the data's origin transparent to the user.
    const insureeNumberField = resolveSourcedValue<string>(
      formData?.insureeNumber,
      patientValues?.insuranceNo,
    );
    const insuranceStatusField = resolveSourcedValue<string>(
      formData?.insuranceStatus,
      patientValues?.status,
    );

    // Derive therapyType from the selected account (Physio vs. Ergo)
    const accountTherapyType = useMemo(() => {
      if (!formData.accountId || !accounts) return "Physiotherapie";
      const selectedAccount = accounts.find(
        (a: any) => a.name === formData.accountId || a.id === formData.accountId
      );
      return selectedAccount?.therapyType || "Physiotherapie";
    }, [formData.accountId, accounts]);

    // Only the groups the chosen Rezept-typ can be billed against — narrowed
    // further to the ones this practice's therapy type actually has line items
    // for, so the invariant cannot be broken by hand either.
    const tarifGroupOptions = useMemo(
      () =>
        resolveTarifGroupForRxType(
          safeMasterTablesData,
          formData?.rxtype,
          formData?.tarifGroupId,
          accountTherapyType
        ).options,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [safeMasterTablesData, formData?.rxtype, formData?.tarifGroupId, accountTherapyType]
    );

    // Position und DocumentViewer State-Variablen
    const [position, setPosition] = useState<"default" | "right-third">("default");
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

    const [icdCodeEntries, setIcdCodeEntries] = useState(() => {
      // Ensure `initialrxValues.icdCodeEntries` is an array
      return (initialrxValues.icdCodeEntries || []).map((entry) => ({
        id: entry.id ? entry.id : "",
        userText: entry.userText ? entry.userText.split("") : Array(5).fill(""),
      }));
    });

    useEffect(() => {
      const fetchData = async () => {
        try {
          if (selectedRxId) {
            const response = await getRxNotes(selectedRxId);
            setRxNotes(response);
          }
        } catch (error) {
          // console.log("error", error);
        }
      };

      fetchData();
    }, [selectedRxId, fetchNotes]);
    const updateNote = async (index: number, updatedCard: NoteDto) => {
      updatedCard.rxId = selectedRxId;
      if (updatedCard.id) {
        await updateNoteById(updatedCard.id, user?.id as string, updatedCard);
      } else {
        await postNotes(user?.id as string, updatedCard);
      }
      const updatedNotes = [...rxNotes];
      updatedNotes[index] = updatedCard;
      setRxNotes(updatedNotes);
      // setFetchNotes(!fetchNotes);
    };

    const handleUnsavedChangesWarning = () => {
      handleGenericSubmit();
    };
    const handleCloseRxDialog = () => {
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onCloseRx();
      }
    };
    const handleCloseUnsavedChangesWarning = () => {
      onCloseRx();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };
    const [lines, setLines] = useState(["", ""]); // Initialize with two empty strings

    // useEffect(() => {
    //   if (rxFormData) {
    //     // Populate the form data with values from rxFormData
    //     setFormData(rxFormData);
    //   } else {
    //     // Use empty values for form data
    //    setFormData(initialrxValues)
    //   }
    // }, [rxFormData]);
    // Initialize with two input boxes

    // Expose the submitForm function to the parent component

    const formattedRxData = (rxFormData: any, initialTherapyRxs: any) => {
      const code = (rxFormData.diagnosisGroup?.code || rxFormData.diagnosegroup)?.toUpperCase(); // Example: "PS1"

      // Check if the code is "PS1"
      if (code) {
        // Determine the additional character based on flags
        let additionalChar;
        if (rxFormData.a) {
          additionalChar = "a";
        } else if (rxFormData.b) {
          additionalChar = "b";
        } else if (rxFormData.c) {
          additionalChar = "c";
        } else if (rxFormData.d) {
          additionalChar = "d";
        }

        // Combine the characters and set input symptoms
        const newInputSymptoms = [...code];
        if (additionalChar) {
          newInputSymptoms.push(additionalChar);
        }

        // Update the inputSymptoms state
        setInputSymptoms(newInputSymptoms);
      }
      if (rxFormData.icdCodeEntries.length > 0) {
        // Process the existing entries
        const processedEntries = rxFormData.icdCodeEntries.map((entry: any) => {
          // Remove periods and split userText into an array of characters
          const cleanedUserText = entry.userText.replace(/\./g, ""); // Remove periods
          const splitUserText = cleanedUserText.split(""); // Split into characters

          // Ensure the array has exactly 5 elements, padding with empty strings if necessary
          const paddedUserText = [
            ...splitUserText,
            ...Array(5).fill("").slice(splitUserText.length, 5),
          ];

          return {
            id: entry.id || "",
            userText: paddedUserText,
          };
        });

        // Ensure there are at least two entries
        const defaultEntries =
          processedEntries.length < 2
            ? [...processedEntries, { userText: Array(5).fill("") }]
            : processedEntries;

        setIcdCodeEntries(defaultEntries);
      } else {
        // If there are no entries, set two empty entries
        setIcdCodeEntries([
          { id: "", userText: Array(5).fill("") },
          { id: "", userText: Array(5).fill("") },
        ]);
      }

//       if (rxFormData.diagnosisText) {
//         // Split the diagnosisText on newline characters
//         const splitLines = rxFormData.diagnosisText.split("\n");

//         // Ensure the array has exactly two elements
//         // If less, pad with empty strings; if more, trim to two elements
//         const formattedLines = [
//           splitLines[0] || "", // First line
//           splitLines[1] || "", // Second line (if it exists, otherwise empty string)
//         ];
//         setLines(formattedLines);
//       }
        const text = rxFormData.diagnosisText ?? ""; // Always defined
        const splitLines = text.split("\n");

        const formattedLines = [splitLines[0] || "", splitLines[1] || ""];

        setLines(formattedLines);

      const updatedFormData = {
        ...rxFormData,
        diagnosisText: rxFormData?.diagnosisText || "",
        additionalNotes: rxFormData?.additionalNotes || "",
        additionalNotes2: rxFormData?.additionalNotes2 || "",
        symptoms: rxFormData?.symptoms || "",

        // Keep these Rx-native. The patient-record fallback is applied at the
        // display/save boundary via resolveSourcedValue so provenance (Rx vs
        // patient) stays distinguishable. Patient-only fields (name, DOB) are
        // not snapshotted here and are read directly from patientValues.
        insureeNumber: rxFormData?.insureeNumber || "",
        insuranceStatus: rxFormData?.insuranceStatus || "",
        // Receiptpayer: rxFormData.payer.insurer || "",
        Receiptpayer:
          insurer[0]?.find((option: any) => option.id === rxFormData.Receiptpayer)?.name ||
          rxFormData.payer?.name ||
          rxFormData.Receiptpayer,
        chipcard: rxFormData.chipcard || rxFormData.payer?.ik,
        diagnosegroup:
          diagnoseGroups[0]?.find((option: any) => option.id === rxFormData.diagnosegroup)?.code ||
          rxFormData.diagnosisGroup?.code,
        rxtype:
          rxType[0]?.find((option: any) => option.id === rxFormData.rxtype)?.text ||
          rxFormData.rxtype?.text ||
          "Kasse",
        accountId:
          account[0]?.find((option: any) => option.id === rxFormData.accountId)?.name ||
          rxFormData.account?.name,
        status: rxFormData.status || "",
        signatureStatus: rxFormData.signatureStatus || "",
        therapyRxs: initialTherapyRxs,
        // rxtype:rxFormData.rxtype?.id || "",
        // accountId:rxFormData.account?.id || "",
        // chipcard: rxFormData.payer?.id || "",
        // doctorId: rxFormData.doctor?.contactInfo?.lastName || "",
        bsnr: rxFormData.clinicId || rxFormData.practiceNumber || "",
        // diagnosegroup: rxFormData.diagnosisGroup?.code || "",
        // Map new API field names to form's internal keys
        doctorNumber: rxFormData.LANR || "",
        practiceNumber: rxFormData.BSNR || "",
        doctorId: rxFormData.doctorId || rxFormData.doctorNumber || "",
        clinicId: rxFormData.clinicId || rxFormData.practiceNumber || "",
      };
      return updatedFormData;
    };

    useEffect(() => {
      const fetchData = async () => {
        if (rxFormData && rxFormData.rxNumber) {
          // rxFormData = patientValues?.rxs.find((rx: any) => rx.id === rxFormData.id);

          // let rxs: [] = [];
          let icdCodeEntries;

          if (rxFormData.icdCodeEntries && rxFormData.icdCodeEntries.length > 0) {
            if (rxFormData.icdCodeEntries.length === 1) {
              icdCodeEntries = [...rxFormData.icdCodeEntries, { userText: "" }];
            } else {
              icdCodeEntries = rxFormData.icdCodeEntries;
            }
          } else {
            icdCodeEntries = [{ userText: "" }, { userText: "" }];
          }

          // MD-232: Initialize selectedTherapyTerms from existing therapyRxs
          const existingTerms = new Set<string>();
          const existingDoubledTerms = new Set<string>();
          const existingSeriesByTerm: Record<string, { id?: string; label?: string }> = {};
          let foundMainFreq = "";
          let foundMainAmt = "";
          let foundSuppFreq = "";
          let foundSuppAmt = "";
          
          rxFormData?.therapyRxs?.forEach((therapyRx: any) => {
            // MD-232 format: rawText or prescribedTherapy reference
            const termLabel = therapyRx.rawText 
              || therapyRx.prescribedTherapyId 
              || therapyRx.prescribedTherapy?.label;
    
            if (termLabel) {
              const resolvedTerm = resolveTherapyTermLabel(termLabel);
              existingTerms.add(resolvedTerm);
              if (therapyRx.isDoubled || Number(therapyRx.unitsPerDay) > 1) {
                existingDoubledTerms.add(resolvedTerm);
              }
              const loadedSeries = rxFormData?.rxSeries || therapyRx?.rxSeries || therapyRx?.rx?.rxSeries;
              if (loadedSeries?.label || loadedSeries?.id) {
                existingSeriesByTerm[resolvedTerm] = {
                  id: loadedSeries?.id,
                  label: loadedSeries?.label,
                };
              }
              // Extract frequency/amount for main vs supplementary
              const freq = therapyRx.frequency?.text || therapyRx.frequency?.id || "";
              const amt = therapyRx.amount?.toString() || "";
              if (isSupplementaryTherapy(resolvedTerm)) {
                if (!foundSuppFreq && freq) foundSuppFreq = freq;
                if (!foundSuppAmt && amt) foundSuppAmt = amt;
              } else {
                if (!foundMainFreq && freq) foundMainFreq = freq;
                if (!foundMainAmt && amt) foundMainAmt = amt;
              }
            }
          });
          applyLoadedTherapyUiState({
            terms: existingTerms,
            doubledTerms: existingDoubledTerms,
            seriesByTerm: existingSeriesByTerm,
            mainFreq: foundMainFreq,
            mainAmt: foundMainAmt,
            suppFreq: foundSuppFreq,
            suppAmt: foundSuppAmt,
          });

          // Keep therapyRxs in new format (rawText, prescribedTherapy, prescribedTherapyId)
          rxFormData.therapyRxs = rxFormData?.therapyRxs?.map((therapyRx: any) => ({
            id: therapyRx.id,
            date: rxFormData.date,
            rawText: therapyRx.rawText,
            prescribedTherapyId: therapyRx.prescribedTherapyId,
            prescribedTherapy: therapyRx.prescribedTherapy,
            amount: therapyRx.amount,
            frequency: therapyRx.frequency
              ? therapyRx.frequency.id
                ? therapyRx.frequency
                : { id: therapyRx?.frequency, text: therapyRx?.frequency }
              : null,
            isDoubled: therapyRx.isDoubled,
            rxSeries: rxFormData.rxSeries ?? therapyRx.rx?.rxSeries,
          }));

          const initialTherapyRxs = Array.from({ length: 3 }, (_, index) => {
            if (rxFormData?.therapyRxs && index < rxFormData.therapyRxs.length) {
              // If index is within the length of formData.therapyRxs, use existing data
              return rxFormData.therapyRxs[index];
            } else {
              // Otherwise, add an empty row with MD-232 format
              return {
                id: uuidv4(),
                amount: "",
                frequency: { id: "", text: "", prefferedValue: "" },
                rawText: null,
                prescribedTherapyId: null,
                prescribedTherapy: null,
                isDoubled: false,
              };
            }
          });
          // Update form data based on fetched data

          const updatedFormData = formattedRxData(rxFormData, initialTherapyRxs);
          // Update form data state
          setFormData(updatedFormData);

          // Optionally, return the updated form data
          return updatedFormData;
        }
        // Make API request to fetch patient data based on patient ID or any other identifier
        else {
          // MD-232: Also initialize selectedTherapyTerms in else branch
          const existingTerms = new Set<string>();
          const existingDoubledTerms = new Set<string>();
          const existingSeriesByTerm: Record<string, { id?: string; label?: string }> = {};
          let foundMainFreq = "";
          let foundMainAmt = "";
          let foundSuppFreq = "";
          let foundSuppAmt = "";

          rxFormData?.therapyRxs?.forEach((therapyRx: any) => {
            //This may be from genric form where therapy object still exists, so check for rawText or prescribedTherapy reference
            if (therapyRx.therapy?.abbreviation) {
              therapyRx.rawText = therapyRx.therapy.abbreviation;
            }
            // MD-232 format: rawText or prescribedTherapy reference
            const termLabel = therapyRx.rawText 
              || therapyRx.prescribedTherapyId 
              || therapyRx.prescribedTherapy?.label

            if (termLabel) {
              const resolvedTerm = resolveTherapyTermLabel(termLabel);
              existingTerms.add(resolvedTerm);
              if (therapyRx.isDoubled || Number(therapyRx.unitsPerDay) > 1) {
                existingDoubledTerms.add(resolvedTerm);
              }
              const loadedSeries = rxFormData?.rxSeries || therapyRx?.rxSeries || therapyRx?.rx?.rxSeries;
              if (loadedSeries?.label || loadedSeries?.id) {
                existingSeriesByTerm[resolvedTerm] = {
                  id: loadedSeries?.id,
                  label: loadedSeries?.label,
                };
              }
              const freq = therapyRx.frequency?.text || therapyRx.frequency?.id || "";
              const amt = therapyRx.amount?.toString() || "";
              if (isSupplementaryTherapy(resolvedTerm)) {
                if (!foundSuppFreq && freq) foundSuppFreq = freq;
                if (!foundSuppAmt && amt) foundSuppAmt = amt;
              } else {
                if (!foundMainFreq && freq) foundMainFreq = freq;
                if (!foundMainAmt && amt) foundMainAmt = amt;
              }
            }
          });
          applyLoadedTherapyUiState({
            terms: existingTerms,
            doubledTerms: existingDoubledTerms,
            seriesByTerm: existingSeriesByTerm,
            mainFreq: foundMainFreq,
            mainAmt: foundMainAmt,
            suppFreq: foundSuppFreq,
            suppAmt: foundSuppAmt,
          });

          // If no selectedRow, check if therapytableData exists - MD-232 format
          const therapyRxs: any =
            rxFormData?.therapyRxs && rxFormData?.therapyRxs?.length
              ? Array.from({ length: 3 }, (_, index) => ({
                  ...rxFormData?.therapyRxs?.[index],
                  id: rxFormData?.therapyRxs?.[index]?.id || uuidv4(),
                  amount: rxFormData?.therapyRxs?.[index]?.amount || "",
                  frequency: rxFormData?.therapyRxs?.[index]?.frequency
                    ? rxFormData?.therapyRxs?.[index]?.frequency
                    : { id: "", text: "" },
                  rawText: rxFormData?.therapyRxs?.[index]?.rawText || null,
                  prescribedTherapyId: rxFormData?.therapyRxs?.[index]?.prescribedTherapyId || null,
                  prescribedTherapy: rxFormData?.therapyRxs?.[index]?.prescribedTherapy || null,
                }))
              : initialrxValues.therapyRxs;

          const updatedData = formattedRxData(rxFormData, therapyRxs);

          // Set form values using therapytableData or default to initialrxValues
          setFormData(updatedData);
        }
      };
      fetchData();

      // Only Rx-native data is snapshotted into formData now, so this effect just
      // tracks rxFormData. Patient-record fields (name, DOB, and the insurance
      // fallbacks) are read directly from patientValues at render time, which also
      // removes the prior timing bug where the patient loading after the Rx left
      // the name blank when navigating directly to an Rx.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rxFormData]);
    // Update symptoms based on diagnosis group and the fourth character
    // Update symptoms based on diagnosis group and the selected boolean flags
    useEffect(() => {
      if (formData.diagnosegroup) {
        const diagnoseGroupIsId = diagnoseGroups[0].some(
          (group: any) => group.id === formData.diagnosegroup
        );
        // Find the matching diagnose group based on the selected code
        const selectedDiagnoseGroup = diagnoseGroupIsId
          ? diagnoseGroups[0].find((group: any) => group.id === formData.diagnosegroup)
          : diagnoseGroups[0].find((group: any) => group.code === formData.diagnosegroup);

        if (selectedDiagnoseGroup) {
          // Generate symptoms based on selected checkboxes
          let selectedSymptoms = "";
          if (formData.a) selectedSymptoms += selectedDiagnoseGroup.a + " ";
          if (formData.b) selectedSymptoms += selectedDiagnoseGroup.b + " ";
          if (formData.c) selectedSymptoms += selectedDiagnoseGroup.c + " ";
          if (formData.d) selectedSymptoms += selectedDiagnoseGroup.d + " ";
          // If diagnose group length is 4 and a, b, c, d are all false, concatenate all subgroup text
          if (
            selectedDiagnoseGroup.code?.length === 4 &&
            !formData.a &&
            !formData.b &&
            !formData.c &&
            !formData.d
          ) {
            selectedSymptoms +=
              (selectedDiagnoseGroup.a ? selectedDiagnoseGroup.a + " " : "") +
              (selectedDiagnoseGroup.b ? selectedDiagnoseGroup.b + " " : "") +
              (selectedDiagnoseGroup.c ? selectedDiagnoseGroup.c + " " : "") +
              (selectedDiagnoseGroup.d ? selectedDiagnoseGroup.d + " " : "");
          }

          setSelectedDiagnoseGroup(selectedDiagnoseGroup);
          setFormData((prevValues: any) => ({
            ...prevValues,
            a: selectedDiagnoseGroup.a ? prevValues.a : false,
            b: selectedDiagnoseGroup.b ? prevValues.b : false,
            c: selectedDiagnoseGroup.c ? prevValues.c : false,
            d: selectedDiagnoseGroup.d ? prevValues.d : false,
            ...(prevValues.hasIndividualSymptoms ? {} : { symptoms: selectedSymptoms.trim() }),
          }));
        }
      }
    }, [formData.diagnosegroup, formData.a, formData.b, formData.c, formData.d]);
    const handleICDCodeEntrySubmit = useCallback(
      async (icdEntry: any, index: number) => {
        try {
          setFormData((prevValues: any) => {
            const updatedICDCodeEntries = prevValues.icdCodeEntries.map(
              (icdEntry: any, i: number) => {
                return i === index
                  ? { id: icdEntry?.id || "", userText: icdEntry?.userText }
                  : icdEntry;
              }
            );

            // Update concatenated diagnosisText
            const concatenatedDiagnosisText = prevValues.diagnosisText
              ? `${prevValues.diagnosisText}\n${icdObject.text}` // Append the new icdObject.text to the existing diagnosisText with a new line
              : icdObject.text;
            return {
              ...prevValues,
              icdCodeEntries: updatedICDCodeEntries,
              diagnosisText: concatenatedDiagnosisText.trim(), // Set diagnosisText to the concatenated value
            };
          });
          setLines((prevLines) => {
            const newLines = [...prevLines];
            newLines[index] = icdObject.text; // Update the specific line
            return newLines;
          });

          // Update individual line for the compartment inputs
          setLines((prevLines) => {
            const newLines = [...prevLines];
            newLines[index] = icdObject.text;
            return newLines;
          });
        } catch (error) {
          console.error("Error updating ICD entry:", error);
        }
      },
      [icdObject]
    );

    // Trigger submission when icdObject is updated for the current ICD
    useEffect(() => {
      if (icdObject && currentICD) {
        const index = icdCodeEntries.findIndex((e) => {
          const code = Array.isArray(e.userText) ? e.userText.join("") : e.userText || "";

          // Insert dot if length >= 4
          const formatted =
            code.length >= 4 && !code.includes(".") ? `${code.slice(0, 3)}.${code.slice(3)}` : code;

          return formatted === currentICD;
        });

        if (index !== -1) {
          handleICDCodeEntrySubmit(icdCodeEntries[index], index);
        }
      }
    }, [icdObject]);

    const handleICDData = (icdCode: any, index: number) => {
      const userTextArray = Array.isArray(icdCodeEntries[index].userText)
        ? icdCodeEntries[index].userText
        : (icdCodeEntries[index].userText as any)?.split("");

      let formattedUserText = userTextArray.join("");

      if (formattedUserText.length >= 4 && !formattedUserText.includes(".")) {
        formattedUserText = `${formattedUserText.slice(0, 3)}.${formattedUserText.slice(3)}`;
      }

      setCurrentICD(formattedUserText);
    };

    // const handleICDCodeEntrySubmit = async (icdCode: any, index: number) => {
    //   try {
    //     // Access userText from the current icdCodeEntry
    //     const userTextArray: any = formData.icdCodeEntries[index].userText;
    //     let formattedUserText = userTextArray;

    //     // Insert period if the 4th character is present
    //     if (userTextArray.length >= 4 && !formattedUserText.includes(".")) {
    //       formattedUserText = `${formattedUserText.slice(0, 3)}.${formattedUserText.slice(3)}`;
    //     }

    //     // Log the formatted userText

    //     // TODO: Replace with useIcdCodes hook or appropriate solution
    //     // Fetch ICD object using the formatted string
    //     // const icdObject: any = await getICD(formattedUserText);
    //     const icdObject: any = null; // Temporary placeholder
    //     if (icdObject) {
    //       // Update the icdCodeEntries state with the formatted userText
    //       setFormData((prevValues: any) => {
    //         const updatedICDCodeEntries = prevValues.icdCodeEntries.map(
    //           (icdEntry: any, i: number) => {
    //             return i === index
    //               ? { id: icdCode?.id || "", userText: formattedUserText }
    //               : icdEntry;
    //           }
    //         );

    //         // Update concatenated diagnosisText
    //         const concatenatedDiagnosisText = prevValues.diagnosisText
    //           ? `${prevValues.diagnosisText}\n${icdObject.text}` // Append the new icdObject.text to the existing diagnosisText with a new line
    //           : icdObject.text;
    //         return {
    //           ...prevValues,
    //           icdCodeEntries: updatedICDCodeEntries,
    //           diagnosisText: concatenatedDiagnosisText.trim(), // Set diagnosisText to the concatenated value
    //         };
    //       });
    //       setLines((prevLines) => {
    //         const newLines = [...prevLines];
    //         newLines[index] = icdObject.text; // Update the specific line
    //         return newLines;
    //       });
    //     }
    //   } catch (error) {
    //     console.error("Error fetching ICD object:", error);
    //   }
    // };

    const handleChange = (event: any, index?: any) => {
      const { name, value } = event.target;
      const updatedTherapyRxs: any = [...formData.therapyRxs];
      updatedTherapyRxs[index][name.split(".")[1]] = value;
      setFormData({ ...formData, therapyRxs: updatedTherapyRxs });
    };
    const handleOpenAddEditDialog = (lanr: string | null, bsnr: string | null) => {
      setAddEditDialogOpen(true);
    };

    const handleCloseAddEditDialog = (data: any) => {
      // Handle doctor data, LANR, and BSNR passed back from DoctorDialog
      setAddEditDialogOpen(false);

      // Here you can perform further actions with the received data
      if (data && "number" in data) {
        const { number, clinicNumber, contactInfo, id } = data;

        // Update states or perform other operations based on the received data
        setSelectedDoctor(contactInfo?.lastName);
        setFormData((prevFormValues: any) => ({
          ...prevFormValues,
          doctorId: id || number,
          clinicId: data.clinicId || clinicNumber,
          doctorNumber: number,
          practiceNumber: clinicNumber,
        }));

        // For example, you can update doctor form values if needed
        // setDoctorFormValues(doctorData);
      }
    };

    const [diagnosisText, setDiagnosisText] = useState("");

    const processDiagnosis = () => {
      const diagnosisGroup = formData.a + formData.b + formData.c + formData.d;

      if (diagnosisGroup.length === 4) {
        setDiagnosisText(diagnosisGroup); // Full group, no need to check further
      } else if (diagnosisGroup.length === 3) {
        const char = formData.d;
        if (["a", "b", "c", "d"].includes(char)) {
          setDiagnosisText(diagnosisGroup); // 3-character group + valid 4th char
        } else {
          alert("Invalid 4th character. Must be a, b, c, or d.");
        }
      } else {
        alert("Invalid Diagnosis Group length. Ensure correct input.");
      }
    };
    const [isHausbesuchSelected, setHausbesuchSelected] = useState<boolean | null>(
      formData.isHomeVisit
    );
    const handleCheckboxChange = (event: any) => {
      const selectedValue = event.target.value; // "yes" or "no"

      if (selectedValue === "yes") {
        setHausbesuchSelected(true);
        // Set isHomeVisit to true if "yes" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: true,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else if (selectedValue === "no") {
        setHausbesuchSelected(false);
        // Set isHomeVisit to false if "no" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: false,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setHausbesuchSelected(null); // Set null if neither is selected
        // Set isHomeVisit to null if neither checkbox is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: null,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleInputDiagnosisChange = (e: any, key: any) => {
      setFormData({ ...formData, [key]: e.target.value });
    };

    const [inputSymptoms, setInputSymptoms] = useState(["", "", "", ""]);
    const inputRefs: any = useRef([]);

    // Ensure inputRefs.current is correctly populated
    useEffect(() => {
      inputRefs.current = {};
    }, [formData.icdCodeEntries.length]);

    // const updateRxProcessingStatus = async (event: any) => {
    //   const response = await updateprocessingStatus(rxFormData.id, event?.target.value);
    // };
    const handleDeleteTherapy = (therapyId: string) => {
      // Filter out the therapy that matches the given ID
      const updatedTherapies = formData.therapyRxs.filter((therapy) => therapy.id !== therapyId);

      // Update formData state with new therapy list
      setFormData({
        ...formData,
        therapyRxs: updatedTherapies,
      });
    };

    const handlePracticeNumberInteraction = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("BSNR muss 9-stellig sein", "error");
        return;
      }

      if ((e.type === "blur" || isTriggerKey) && (value.length === 9 || value.length === 0)) {
        // setIsDoctorNumberDisabled(false);
        try {
          if (value.length === 9) {
            const clinicData = (await getClinicById(value)) as ClinicForModal;
            if (clinicData) {
              setDoesClinicExist(true);
              setCurrentClinic(clinicData);
              setFormData((prev) => ({ ...prev, clinicId: clinicData.id }));
              setShowClinicData(true);
            } else {
              // const inputValue = selectedBSNR || formData?.practiceNumber;
              setDoesClinicExist(false);
              setCurrentClinicId(value);
              setCurrentDoctorId(currentDoctor?.id || "");
              setShowClinicData(false);
              setCurrentClinic(undefined);
              setIsClinicModalOpen(true);
            }
          } else if (value.length === 0) {
            setCurrentClinic(undefined);
          }
        } catch (error) {}
      } else {
      }
    };

    const handleGetDoctorById = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("Mindestens 9 Zeichen erforderlich", "error");
        return;
      }

      if ((e.type === "blur" || isTriggerKey) && (value.length === 9 || value.length === 0)) {
        try {
          if (value.length === 9) {
            const doctorData: any = await getDoctorById(value);
            // console.log("doctor DATA -------> ", doctorData);
            if (doctorData) {
              setDoesDoctorExist(true);
              setCurrentDoctor(doctorData);
              setFormData((prev) => ({ ...prev, doctorId: doctorData.id }));
              // Only check association if currentClinic is set and valid
              if (currentClinic && currentClinic.id) {
                const isDoctorInClinic = doctorData.clinics?.some(
                  (clinic: any) => clinic.id === currentClinic.id
                );
                if (isDoctorInClinic) {
                  notificationStore.showMessage(
                    "This doctor exists and is already associated with this clinic",
                    "success"
                  );
                } else {
                  const confirmed = await confirmDoctorClinicJoin(doctorData, currentClinic);
                  if (!confirmed) {
                    // User cancelled — clear the doctor selection (treat as a mistake).
                    setCurrentDoctor(undefined);
                    setDoesDoctorExist(false);
                    setFormData((prev) => ({ ...prev, doctorId: "", doctorNumber: "" }));
                    return;
                  }
                  notificationStore.showMessage(
                    "This doctor exists already and will now get associated with this clinic",
                    "success"
                  );
                  handleAssociateDoctorToClinic(doctorData);
                }
              } else {
                // No clinic entered yet, just set doctor, do not associate
                notificationStore.showMessage(
                  "Bitte geben Sie zuerst eine gültige Betriebsstätten-Nr. (BSNR) ein.",
                  "warning"
                );
              }
            } else {
              setDoesDoctorExist(false);
              setIsClinicModalOpen(true);
              setCurrentDoctorId(value);
              setCurrentClinicId(currentClinic?.id || "");
            }
          } else if (value.length === 0) {
            setCurrentDoctor(undefined);
          }
        } catch (error) {}
      }
    };

    enum ReportMethod {
      Email = "Email",
      Fax = "Fax",
      Post = "Post",
    }

    const handleAssociateDoctorToClinic = async (doctorData: any) => {
      const createDoctorClinicDto = {
        id: doctorData?.id || "",
        contactInfo: {
          salutation: doctorData?.contactInfo?.salutation || "",
          firstNames: doctorData?.contactInfo?.firstNames || "",
          lastName: doctorData?.contactInfo?.lastName || "",
        },
        clinics: [
          {
            id: currentClinic?.id || "",
            contactInfo: {
              businessName: currentClinic?.contactInfo?.businessName || "",
              phone1: currentClinic?.contactInfo?.phone1 || "",
              fax: currentClinic?.contactInfo?.fax || "",
              addressLine1: currentClinic?.contactInfo?.addressLine1 || "",
              addressLine2: currentClinic?.contactInfo?.addressLine2 || "",
              city: currentClinic?.contactInfo?.city || "",
              zip: currentClinic?.contactInfo?.zip || "",
              email: currentClinic?.contactInfo?.email || "",
            },
            notes: currentClinic?.notes || "",
            methodForDrReport: (currentClinic?.methodForDrReport as ReportMethod) || "Email",
            methodForRxOrder: (currentClinic?.methodForRxOrder as ReportMethod) || "Email",
          },
        ],
      };

      // Call createDoctorWithClinic to save data
      // setLoading(true);
      try {
        const response = await createDoctorWithClinic(createDoctorClinicDto);
        return response;
      } catch (error) {
        console.error("Error saving doctor and clinic:", error);
      } finally {
        setLoading(false);
      }
    };

    const handleInputChange = (
      event: any,
      index?: any,
      idValue?: string,
      compartmentIndex?: string
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, value, checked, type, id } = event.target;
      setUnsavedChanges(true);

      const inputValue = type === "checkbox" ? checked : value;

      if (name === "practiceNumber") {
        if (value.length > 9) return;
      }

      if (name === "doctorNumber") {
        if (value.length > 9) return;
        // Only update the field, do not clear doctor info here!
        setFormData((prevValues: any) => ({
          ...prevValues,
          doctorNumber: value,
        }));
        return;
      }

      if (name === "date" && formData.isPerpetual && value) {
        setFormData((prevValues: any) => ({
          ...prevValues,
          unvirtualize: true,
        }));
      }

      const updatedTherapyRxs = [...formData.therapyRxs];

      if (name === "therapy") {
        // MD-232: the line names the prescribed TERM as the text that was
        // typed, and the server resolves it text -> alias -> term on save.
        // Previously this looked the value up among tarif-bound BILLABLE LINE
        // ITEMS and wrote state only on a hit — so a real term like "KG-ZNS",
        // which is no line item's abbreviation, stored nothing at all and the
        // save reached the server naming no term ("Heilmittel unbekannt").
        // `abbreviation` is kept in step because the table cell reads it.
        if (value) {
          updatedTherapyRxs[index] = {
            ...updatedTherapyRxs[index]
            // frequency: selectedTherapy.frequency,
          };
        } else {
          // Cleared: drop the term as well as the display value.
          updatedTherapyRxs[index] = {
            ...updatedTherapyRxs[index]
          };
        }
        setFormData((prevformValues: any) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name === "amount") {
        updatedTherapyRxs[index] = {
          ...updatedTherapyRxs[index],
          amount: value,
        };

        setFormData((prevformValues) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      if (name === "frequency") {
        updatedTherapyRxs[index] = {
          ...updatedTherapyRxs[index],
          frequency: {
            id: value,
            text: value,
          },
        };

        setFormData((prevformValues) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name === "rxSeries") {
        // Update therapyRxs based on rowIndex and therapyId
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
          if (index === index && therapy.id === idValue) {
            return {
              ...therapy,
              rxSeries: { id: id, label: value },
            };
          }
          return therapy;
        });

        // Update the formData state with the updated therapyRxs
        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name.startsWith("icdCodeEntries")) {
        const newValue = value.toUpperCase().slice(0, 1);

        // Update the specific compartment in the ICD code entry
        setFormData((prevValues) => {
          const updatedICDCodeEntries = [...prevValues.icdCodeEntries];
          const updatedUserText = [...updatedICDCodeEntries[index].userText];
          updatedUserText[compartmentIndex as any] = newValue;
          updatedICDCodeEntries[index].userText = updatedUserText.join("");

          return { ...prevValues, icdCodeEntries: updatedICDCodeEntries };
        });

        // Automatically move focus to the next compartment
        if (newValue && (compartmentIndex as any) < 4) {
          inputRefs.current[`${index}-${(compartmentIndex as any) + 1}`]?.focus();
        }
      }

      if (name === "rxtype") {
        // The Rezept-typ DECIDES the tarif group. Without this the group the Rx
        // was opened with survived the type change, so a type switched to
        // Privat here was still billed against the old group.
        const selectedRxTypeId = String(id ? id : inputValue);
        const { nextTarifGroupId, requiresChoice } = resolveTarifGroupForRxType(
          safeMasterTablesData,
          selectedRxTypeId,
          formData?.tarifGroupId,
          accountTherapyType
        );
        setFormData((prevValues) => ({
          ...prevValues,
          diagnosegroup: null, // Clear diagnosegroup when rxtype changes
          tarifGroupId: nextTarifGroupId,
        }));
        if (requiresChoice) {
          notificationStore.showMessage(
            "Bitte wählen Sie eine Tarifgruppe für den gewählten Rezept-Typ.",
            "warning"
          );
        }
        filterDiagnoseGroups(selectedRxTypeId);
      }

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   // setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //       doctorData:selectedDoctor
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      // Leitsymptomatik: the a/b/c/d codes and the patientenindividuelle flag are
      // independent — a Verordnung can require both, so neither clears the other.
      // Update form fields based on their names
      setFormData((prevValues: any) => ({
        ...prevValues,
        [name]: id ? id : inputValue,
      }));
      if (name === "inputSymptoms") {
        const newInputDiagnose = [...inputSymptoms];
        newInputDiagnose[index] = value.slice(0, 1); // Only accept one character per input

        // Update the inputDiagnose state
        setInputSymptoms(newInputDiagnose);

        // Move focus to the next input if the current input is not empty and is not the last one
        if (index < 3 && value.length > 0) {
          inputRefs.current[index + 1].focus();
        }

        // Construct the new diagnose group
        const newDiagnoseGroup = newInputDiagnose.join("");

        // Extract the diagnose group and subgroup
        const diagnosegroup = newDiagnoseGroup.slice(0, 3);

        const subgroup = newDiagnoseGroup.length === 4 ? newDiagnoseGroup[3] : "";

        // Determine boolean flags based on the fourth character
        const isA = subgroup === "a";
        const isB = subgroup === "b";
        const isC = subgroup === "c";
        const isD = subgroup === "d";

        setFormData((prevValues: any) => ({
          ...prevValues,
          diagnosegroup: isA || isB || isC || isD ? diagnosegroup : newDiagnoseGroup, // Set the full diagnose group if no A, B, C, or D
          a: isA,
          b: isB,
          c: isC,
          d: isD,
        }));

        const valueToCheck =
          isA || isB || isC || isD ? diagnosegroup.toUpperCase() : newDiagnoseGroup.toUpperCase();

        const options = diagnoseGroups[0].filter((option: any) => option.RxType?.id === "Zahnarzt");
        // Extract valid codes from options
        const validCodes = options.map((option: any) => option.code.toUpperCase());

        // Check if newDiagnoseGroup is a valid code
        if (newDiagnoseGroup.length === 4 && !validCodes.includes(valueToCheck.toUpperCase())) {
          notificationStore.showMessage("Ungültige Diagnosegruppe", "error");
        }
      }
      if (name.startsWith("diagnosisText")) {
        // Update the specific line in the diagnosis text array
        setLines((prevLines) => {
          const newLines = [...prevLines];
          newLines[index] = value;

          // Update the formData with the joined lines
          setFormData((prevValues) => ({
            ...prevValues,
            diagnosisText: newLines.join("\n"),
          }));

          return newLines;
        });
      }
      if (name.startsWith("symptoms")) {
        // Update the specific line in the diagnosis text array

        // Update the formData with the joined lines
        setFormData((prevValues) => ({
          ...prevValues,
          symptoms: value,
        }));
      }

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      if (insurer[0]) {
        if (name === "Receiptpayer") {
          // Autopopulate doctor and bsnr based on LANR selection

          const selectedPayer: any = insurer[0].find(
            (option: any) => option.id === id || option.ik === value
          );
          if (selectedPayer) {
            setFormData((prevFormValues: any) => ({
              ...prevFormValues,
              chipcard: selectedPayer?.ik,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));
            setSelectedChipCard(selectedPayer.ik);
          }
        }

        // if (name === "chipcard") {
        //   const selectedPayer: any = insurer[0].find((option: any) => option.ik === value);
        //   if (selectedPayer) {
        //     setFormData((prevFormValues: any) => ({
        //       ...prevFormValues,
        //       Receiptpayer: selectedPayer.id,
        //       tarifGroupId: selectedPayer?.tarifGroup?.id,
        //     }));

        //     setSelectedPayer(selectedPayer.name);
        //   }
        // }
      }

      setFormData((prevFormData) => {
        const newFormData = { ...prevFormData, [name]: value };
        debounceValidation(newFormData);
        return newFormData;
      });
    };
    const handleChipCardDataEntry = (event: React.KeyboardEvent | React.FocusEvent) => {
      rxvalidate();
      const chipCardValue = (event.currentTarget as HTMLInputElement).value;
      if (
        (event as React.KeyboardEvent).key === "Enter" ||
        event.type === "blur" ||
        event.type === "mouseleave"
      ) {
        // Check if the chipcard number is valid
        const selectedPayer = insurer[0]?.find((option: any) => option.ik === chipCardValue);
        if (selectedPayer) {
          // If valid, autofill payer info
          setFormData((prevFormValues: any) => ({
            ...prevFormValues,
            Receiptpayer: selectedPayer?.id,
            // tarifGroupId: selectedPayer?.tarifGroup?.id,
          }));
          setSelectedPayer(selectedPayer.name);
          notificationStore.showMessage(
            `${selectedPayer?.id} ist ein IK für ${selectedPayer?.name}`,
            "success"
          );
        } else if (chipCardValue) {
          // Show error if chipcard is invalid
          notificationStore.showMessage(
            "Bitte geben Sie eine gültige Chipkartennummer ein.",
            "warning"
          );
          // Pass all form values with updated chipcard value for validation
        }
      }
    };

    useEffect(() => {
      rxvalidate();
    }, [formData]);
    const rxvalidate = (fieldValues = formData) => {
      let temp: any = { ...rxerrors };
      // Validate chipcard (if chipcard exists in fieldValues)
      if ("chipcard" in fieldValues) {
        const chipCardValue = fieldValues.chipcard;
        if (chipCardValue && !insurer[0]?.some((option: any) => option.ik === chipCardValue)) {
          temp.chipcard = "Ungültige Chipkartennummer";
        } else {
          temp.chipcard = ""; // Clear error if valid
        }
      }
      if ("accountId" in fieldValues) temp.accountId = fieldValues.accountId ? "" : "Pflichtfeld";
      if ("tarifGroupId" in fieldValues)
        temp.tarifGroupId = fieldValues.tarifGroupId ? "" : "Pflichtfeld";

      rxsetErrors({
        ...temp,
      });

      if (fieldValues == formData) return Object.values(temp).every((x) => x == "");
    };
    const { rxerrors, rxsetErrors } = rxuseForm(formData, true, rxvalidate);
    const [validationData, setValidationData] = useState<any>({});
    const debounceValidation = useCallback(
      debounce(async (currentFormData) => {
        const data = await getLiveValidation(currentFormData);
        setValidationData(data);
      }, 100),
      []
    );
    const handleICDInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index: number,
      compartmentIndex: number
    ) => {
      const { value } = event.target;
      const newValue = value.toUpperCase().slice(0, 1);
      setUnsavedChanges(true);

      setIcdCodeEntries((prevCompartments) => {
        // Make a copy of the previous state
        const updatedCompartments = [...prevCompartments];

        // Check if the index exists
        if (index >= updatedCompartments.length) {
          console.error(`Index ${index} is out of bounds.`);
          return updatedCompartments;
        }

        // Ensure `userText` is an array
        const compartment = updatedCompartments[index];
        if (!compartment.userText || compartment.userText.length !== 5) {
          compartment.userText = Array(5).fill("");
        }

        // Update the value
        compartment.userText[compartmentIndex] = newValue;

        // Convert to a single string
        let userText = compartment.userText.join("");

        // Insert period if necessary
        if (userText.length > 3 && !userText.includes(".")) {
          userText = `${userText.slice(0, 3)}.${userText.slice(3)}`;
        }

        // Update form data
        setFormData((prevValues) => {
          const updatedICDCodeEntries = [...prevValues.icdCodeEntries];
          updatedICDCodeEntries[index] = { ...updatedICDCodeEntries[index], userText };
          return { ...prevValues, icdCodeEntries: updatedICDCodeEntries };
        });

        return updatedCompartments;
      });

      // Move focus to the next input if necessary
      if (newValue && compartmentIndex < 4) {
        inputRefs.current[`${index}-${compartmentIndex + 1}`]?.focus();
      }
    };

    const handleGenericSubmit = async (event?: any) => {
      if (event) {
        setLoading(false);
        event.preventDefault(); // Prevent default form submission behavior
      }
      try {
        const hasEmptyCharacters = inputSymptoms.some((char) => char === "");
        const hasNonEmptyCharacters = inputSymptoms.some((char) => char !== "");

        if (hasNonEmptyCharacters && hasEmptyCharacters) {
          notificationStore.showMessage(
            "Bitte überprüfen Sie Ihre Diagnosegruppe. Alle vier Felder müssen ausgefüllt sein.",
            "error"
          );
          return;
        }

        // Validate therapy selection, frequency and amount
        // Use the same logic as selectedTherapyTermsForTable to get actual therapies to save
        const therapiesToSave: string[] = [];
        const processedParentGroups = new Set<string>();

        // Handle parent-child groups
        for (const [parent, children] of Object.entries(THERAPY_GROUPS)) {
          const parentSelected = Array.from(selectedTherapyTerms).some(
            t => normalizeTherapyTerm(t) === normalizeTherapyTerm(parent)
          );
          const selectedChild = children.find(child =>
            Array.from(selectedTherapyTerms).some(
              t => normalizeTherapyTerm(t) === normalizeTherapyTerm(child)
            )
          );

          if (selectedChild) {
            therapiesToSave.push(selectedChild);
          } else if (parentSelected) {
            therapiesToSave.push(parent);
          }
          
          processedParentGroups.add(normalizeTherapyTerm(parent));
          children.forEach(c => processedParentGroups.add(normalizeTherapyTerm(c)));
        }

        // Add non-grouped therapies
        Array.from(selectedTherapyTerms).forEach(term => {
          if (!processedParentGroups.has(normalizeTherapyTerm(term))) {
            therapiesToSave.push(term);
          }
        });

        if (therapiesToSave.length === 0) {
          notificationStore.showMessage(
            "Bitte wählen Sie mindestens ein Heilmittel aus.",
            "error"
          );
          return;
        }

        // Check if there are main (non-supplementary) therapies selected
        const hasMainTherapies = therapiesToSave.some(
          (term) => !isSupplementaryTherapy(term)
        );

        // Check if there are supplementary therapies selected
        const hasSuppTherapies = therapiesToSave.some(
          (term) => isSupplementaryTherapy(term)
        );

        // Validate main therapy frequency and amount (required for main therapies)
        if (hasMainTherapies && (!mainTherapyFrequency || !mainTherapyAmount)) {
          notificationStore.showMessage(
            "Bitte geben Sie Häufigkeit und Verordnungsmenge für Hauptheilmittel an.",
            "error"
          );
          return;
        }

        // Validate supplementary therapy frequency and amount (required if supplementary therapies selected)
        if (hasSuppTherapies && (!suppTherapyFrequency || !suppTherapyAmount)) {
          notificationStore.showMessage(
            "Bitte geben Sie Häufigkeit und Verordnungsmenge für ergänzende Heilmittel an.",
            "error"
          );
          return;
        }

        if (rxvalidate()) {
          // The doctor must actually be registered at the practice the Rx names:
          // the stamp takes its telephone number from the PRACTICE, so a
          // mismatched pair prints a stranger's number on a document that leaves
          // the building. Asked here rather than on a field, because the Arzt-Nr
          // check only fires when that field loses focus - correcting the BSNR
          // afterwards, or re-saving an Rx loaded from the database, slips past it.
          if (!(await confirmDoctorClinicMismatch(currentDoctor, currentClinic))) {
            return;
          }
          setLoading(true);

          // Checkboxes carry no row identity, so work out which saved line each
          // ticked term belongs to before building the payload.
          //
          // Read from rxFormData, NOT formData: formData.therapyRxs is capped at
          // three rows (see the Array.from({ length: 3 }) padding on load), so an
          // Rx with four prescribed lines hides the fourth. The diff would then
          // see that line's term as "new" and hand it a disappearing line's id —
          // e.g. removing "Heiße Rolle" made the untouched "Heißluft" inherit its
          // id, and the real Heißluft line got deleted instead.
          // Labels are canonicalised to the checkbox vocabulary — see
          // existingTherapyLinesOf for why the raw spelling is not enough.
          const existingTherapyLines = existingTherapyLinesOf(
            rxFormData?.therapyRxs ?? formData.therapyRxs,
            resolveTherapyTermLabel
          );

          const { idByTerm: therapyLineIds, replacements } = diffTherapyLines(
            selectedTherapyTermsForTable,
            existingTherapyLines,
            {
              normalize: normalizeTherapyTerm,
              isSupplementary: isSupplementaryTherapy,
              parentOf: getParentForChild,
            }
          );

          // A replacement re-uses the outgoing Heilmittel's prescription line, so
          // its already-planned Termine move across to the new therapy. Say so —
          // from the checkboxes alone that consequence is invisible.
          if (replacements.length > 0) {
            const changes = replacements
              .map((replacement) => `${replacement.fromLabel} → ${replacement.toLabel}`)
              .join(", ");
            notificationStore.showMessage(
              `Heilmittel wird geändert: ${changes}. Bereits geplante Termine werden übernommen.`,
              "warning"
            );
          }

          const updatedRxValues: any = {
            ...formData,
            // `a`-`d` are boolean columns and `date` is a date column, both
            // nullable — but the form holds "" for anything it was not given,
            // and Postgres rejects "" outright rather than reading it as
            // "not stated". The Rx then never saves and the dialog closes,
            // taking the entry with it.
            //
            // Empty is ordinary here: of 15,365 prescriptions in production
            // 4,030 carry no date and 3,737 no `a`. Planning before the paper
            // prescription arrives is the normal workflow.
            //
            // Seen first on the Blankoverordnung (MD-514 follow-up). These
            // three forms carry the identical initial values and the identical
            // `{...formData}` spread — they survive only when the Leitsymptom
            // effect happens to run, which it does not when no Diagnosegruppe
            // is chosen. Fixing them together so the fault cannot simply move.
            a: emptyToNull(formData.a),
            b: emptyToNull(formData.b),
            c: emptyToNull(formData.c),
            d: emptyToNull(formData.d),
            date: emptyToNull(formData.date),
            id: rxFormData?.id || "",
            rxNumber: rxFormData?.rxNumber || "",
            LANR: formData.doctorNumber || "",
            BSNR: formData.practiceNumber || "",
            // Preserve the existing fallback-to-patient behavior on save. Provenance
            // is tracked via resolveSourcedValue; explicit copy-from-patient handling
            // is owned by the transparency UI work.
            insureeNumber: insureeNumberField.value ?? "",
            insuranceStatus: insuranceStatusField.value ?? "",
            accountId:
              account[0].find((option: any) => option.name === formData.accountId)?.id ||
              formData.accountId,
            rxtype:
              rxType[0].find((option: any) => option.text === formData.rxtype)?.id ||
              formData.rxtype ||
              "Kasse",
            // An FK column: "" is not a missing value to Postgres, it is an id
            // that does not exist. Guarded like the fields above.
            diagnosegroup: emptyToNull(
              diagnoseGroups[0].find((option: any) => option.code === formData.diagnosegroup)
                ?.id || formData.diagnosegroup
            ),

            Receiptpayer:
              insurer[0].find((option: any) => option.id === formData.chipcard)?.id ||
              formData.Receiptpayer,

            // MD-232: Convert selected therapy checkboxes to therapyRxs with rawText
            // The backend will resolve rawText to prescribedTherapyId via alias table
            // Use different frequency/amount for main vs supplementary therapies
            // For parent-child groups: save child if selected, otherwise parent alone
            // Line ids come from the diff above so a swap keeps its appointments.
            therapyRxs: selectedTherapyTermsForTable.map((termLabel) => {
              const isSupp = isSupplementaryTherapy(termLabel);
              const freq = isSupp ? suppTherapyFrequency : mainTherapyFrequency;
              const amt = isSupp ? suppTherapyAmount : mainTherapyAmount;
              const isDoubled = isTherapyDoubled(termLabel);
              const rxSeries = getTherapyRxSeries(termLabel);
              return {
                // Reuses the line a swapped-out Heilmittel left behind, so its
                // already-scheduled appointments stay attached — see therapyLineDiff.
                id: therapyLineIds.get(termLabel) || uuidv4(),
                rawText: termLabel,  // e.g. "KG", "MLD 30", "Heiße Rolle"
                amount: parseInt(amt),
                frequency: { id: freq, text: freq },
                isDoubled,
                unitsPerDay: isDoubled ? 2 : 1,
                rxSeries: rxSeries?.label || rxSeries?.id ? rxSeries : undefined,
              };
            }),
          };
          if (currentDoctor) {
            updatedRxValues.doctorId = (currentDoctor as any).id || formData.doctorId;
            updatedRxValues.LANR = (currentDoctor as any).LANR || formData.doctorNumber || "";
          } else {
            updatedRxValues.doctor = null;
            updatedRxValues.doctorId = null;
            updatedRxValues.doctorNumber = "";
            updatedRxValues.LANR = "";
          }
          if (currentClinic) {
            updatedRxValues.clinicId = currentClinic.id || formData.clinicId;
            updatedRxValues.BSNR = (currentClinic as any).BSNR || formData.practiceNumber || "";
          } else {
            updatedRxValues.clinic = null;
            updatedRxValues.clinicId = null;
            updatedRxValues.practiceNumber = "";
            updatedRxValues.BSNR = "";
          }

          setUnsavedChanges(false);
          setFormData(updatedRxValues);
          if (entryType === EntryType.newRx && handleNewFormData) {
            handleNewFormData(updatedRxValues);
          } else {
            patientSaveRxs({ patientId: patientId!, rxs: [updatedRxValues] },
              {
                onSuccess: () => {
                  if (onRxSave && selectedRxId) {
                    onRxSave(selectedRxId);
                  }
                  onCloseRx();
                }
              }
            );
            
          }
         
          //  await searchPatient({ id: patientValues.id });
        } else {
          notificationStore.showMessage(
            "Bitte füllen Sie alle erforderlichen Felder aus",
            "warning"
          );
        }
      } catch (error: any) {
        console.error("Error saving Rx:", error);
        notificationStore.showMessage(
          error.message || "Fehler beim Speichern des Rezepts",
          "error",
          error
        );
      } finally {
        // Set loading back to false when the API call is complete
        setLoading(false);
        
      }
    };

    const [therapyRxs, setTherapyRxs] = useState(formData.therapyRxs); // State to manage therapyRxs array
    const componentRef = useRef(null);
    const [filteredDiagnoseGroups, setFilteredDiagnoseGroups] = useState([]);

    const filterDiagnoseGroups = (rxtype: any) => {
      const filtered = diagnoseGroups[0].filter(
        (option: any) =>
          option.RxType?.id === rxtype &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );
      setFilteredDiagnoseGroups(filtered);
    };

    useEffect(() => {
      filterDiagnoseGroups(formData.rxtype);
    }, [formData.rxtype, accountTherapyType]);

    // console.log("formData", formData, readOnlyView);

    // useEffect(() => {
    //   const inputValue = selectedBSNR || formData?.practiceNumber;
    //   const isEmpty = !inputValue || inputValue.trim() === "";

    //   if (isEmpty === false) {
    //     setIsDoctorNumberDisabled(false);
    //   }
    // }, [selectedBSNR, formData?.practiceNumber, currentClinic?.id]);

    const FormContent = (
      <Grid
        container
        direction={"row"}
        spacing={1}
        xs={12}
        sx={{ paddingTop: "0px", paddingLeft: "2px", border: "0px dashed blue" }}
      >
        {!readOnlyView ? (
          <Grid item xs={12} md={3} sx={{ border: "0px dashed green" }}>
            <FlatCard title={`Rezept ${rxFormData?.rxNumber || ""}`}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controls.Select
                    name="rxtype"
                    label="Rezept-typ"
                    value={formData?.rxtype}
                    onChange={handleInputChange}
                    options={rxType[0]?.map((option: any) => ({
                      label: option?.text.toString(),
                      value: option?.text.toString(),
                      id: option?.id.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    id={1}
                    name="accountId"
                    label="Mandant"
                    value={formData?.accountId}
                    onChange={handleInputChange}
                    options={accounts?.map((option: any) => ({
                      label: option?.name?.toString(),
                      value: option?.name?.toString(),
                      id: option?.id?.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                    error={rxerrors.accountId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="status"
                    label="Rezeptverbleib"
                    value={formData?.status}
                    onChange={handleInputChange}
                    options={[
                      {
                        label: "noch nicht erstellt",
                        value: "noch nicht erstellt",
                      },
                      { label: "beim Patient", value: "beim Patient" },
                      { label: "beim Therapeut", value: "beim Therapeut" },
                      { label: "vorhanden", value: "vorhanden" },
                    ]}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="tarifGroupId"
                    label="Tarif Group"
                    id={1}
                    value={formData?.tarifGroupId}
                    onChange={handleInputChange}
                    options={tarifGroupOptions.map((value: any) => ({
                      label: value.toString(),
                      value: value.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Checkbox
                    name="isPerpetual"
                    label="Dauerrezept"
                    value={formData?.isPerpetual}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
            </FlatCard>{" "}
            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData.doctorNumber,
                signatureStatus: formData.signatureStatus,
                clinic: currentClinic,
              }}
              validationData={validationData}
              showSignatureCheck={!readOnlyView}
              readOnlyView={readOnlyView}
              onInputChange={handleInputChange}
              onEdit={() => {
                setCurrentClinicId(currentClinic?.id || "");
                setCurrentDoctorId(currentDoctor?.id || (currentDoctor as any)?.id || "");
                setIsClinicModalEditMode(true);
                setIsClinicModalOpen(true);
              }}
              onMigrationComplete={(type, newValue) => {
                if (type === "doctor") {
                  setFormData((prev) => ({ ...prev, doctorNumber: newValue }));
                  const doctorPK = (currentDoctor as any)?.id;
                  if (doctorPK) getDoctorById(doctorPK).then((doc) => { if (doc) setCurrentDoctor(doc as any); });
                } else {
                  setFormData((prev) => ({ ...prev, practiceNumber: newValue }));
                  const clinicPK = currentClinic?.id;
                  if (clinicPK) getClinicById(clinicPK).then((cli) => { if (cli) setCurrentClinic(cli as any); });
                }
              }}
            />
            <FlatCard title="Heilmittel nach Maßgabe des Kataloges" sx={{ marginTop: "8px" }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 0.2, px: 0.4 }}>
                      <Typography className={classes.subText}>Heilmittel</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.2, px: 0.4, width: "30px", textAlign: "center" }}>
                      <Typography className={classes.subText}>Doppelbehandlung</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.2, px: 0.4, width: "140px" }}>
                      <Typography className={classes.subText}>RxSeries</Typography>
                    </TableCell>
                  </TableRow>
                  {selectedTherapyTermsForTable.length === 0 ? (
                    <TableRow>
                      <TableCell sx={{ py: 0.2, px: 0.4, borderBottom: "none" }} colSpan={3}>
                        <Typography className={classes.subText}>
                          Kein Heilmittel ausgewählt
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedTherapyTermsForTable.map((term) => (
                      <TableRow key={`db-side-${term}`}>
                        <TableCell sx={{ py: 0.2, px: 0.4, borderBottom: "none" }}>
                          <Typography>{term}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.2,
                            px: 0.4,
                            borderBottom: "none",
                            width: "44px",
                            textAlign: "center",
                          }}
                        >
                          <Checkbox
                            size="small"
                            sx={{ p: 0 }}
                            checked={isTherapyDoubled(term)}
                            onChange={(e) => handleDoubledTherapyCheckbox(term, e.target.checked)}
                            disabled={readOnlyView}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.2, px: 0.4, borderBottom: "none" }}>
                          <Controls.Select
                            name="rxSeries"
                            id={1}
                            disabled={readOnlyView || !isTherapySelected(term)}
                            value={getTherapyRxSeries(term)?.label || ""}
                            onChange={(event: any) => handleTermRxSeriesChange(term, event)}
                            options={savedSeriesForPatient.map((option: any) => ({
                              label: option.label,
                              value: option.label,
                              id: option.id,
                            }))}
                            style={{ width: "100%", margin: "0px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </FlatCard>
            {/* <FlatCard title="Arzt">
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                >
                  <Controls.Select
                    name="doctor"
                    label="Arzt aus der Liste wählen"
                    id={1}
                    value={selectedDoctor || formData.doctorId}
                    onChange={handleDoctorChange}
                    options={doctorOptions?.map((doctor: any) => ({
                      label: doctor.contactInfo.lastName.toString(),
                      value: doctor.contactInfo.lastName.toString(),
                      id: doctor.id.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    onClick={() => handleOpenAddEditDialog(selectedLANR, selectedBSNR)}
                    disabled={readOnlyView}
                  >
                    Arzt hinzufügen / bearbeiten
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <ValidationTooltip
                    validity={validationData?.signatureValidation?.validity}
                    message={validationData?.signatureValidation?.validity}
                    placement="left"
                    sx={{ zIndex: 1300 }}
                  >
                    <Controls.RadioGroup
                      label={
                        <Typography sx={{ textAlign: "left" }}>
                          Arztunterschrift vorhanden
                        </Typography>
                      }
                      name="signatureStatus"
                      value={formData?.signatureStatus}
                      options={[
                        { id: "nicht kontrolliert", label: "nicht kontrolliert" },
                        { id: "vorhanden", label: "vorhanden" },
                        { id: "fehlend", label: "fehlend" },
                      ]}
                      disabled={readOnlyView}
                      onChange={handleInputChange}
                      sx={{
                        width: "100%",
                        paddingLeft: "1rem",
                        textAlign: "left",
                        fontSize: "0.7rem",
                      }}
                    />
                  </ValidationTooltip>
                </Grid>
              </Grid>

              {isAddEditDialogOpen && (
                <DoctorDialog
                  openAddEditDialog={isAddEditDialogOpen}
                  handleCloseAddEditDialog={handleCloseAddEditDialog}
                  doctorOptions={doctorOptions}
                  selectedLANR={formData.doctorNumber}
                  selectedBSNR={formData.practiceNumber}
                />
              )}
            </FlatCard> */}
           
            {/* <Controls.Input
              name="practiceNumber"
              label="BSNR"
              value={selectedBSNR || formData?.practiceNumber}
              onChange={handleInputChange}
            /> */}
          </Grid>
        ) : (
          <Grid item xs={12} md={4} sx={{ border: "0px dashed green" }}>
            <FlatCard title="Allgemein" sx={{ marginBottom: "2px" }}>
              <Grid container spacing={0} justifyContent="space-between">
                {/* First Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" sx={{ fontSize: "0.8em" }}>
                    Rezept-Typ:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.rxtype || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>

                {/* Second Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Mandant:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.accountId || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem", padding: "0px" }}
                  />
                </Grid>

                {/* Third Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Rx Location:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={
                      formData.isHomeVisit === true
                        ? "Haus/Heim"
                        : formData.isHomeVisit === false
                        ? "Praxis"
                        : "-"
                    }
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="processingStatus"
                    label="Status"
                    onChange={updateRxProcessingStatus}
                    value={formData?.processingStatus || RxDtoProcessingStatusEnum.InBehandlung}
                    options={Object.values(RxDtoProcessingStatusEnum).map((value) => ({
                      value: value,
                      label: value,
                    }))}
                    noTriggerChange={true}
                  />
                </Grid>
              </Grid>
            </FlatCard>
            <FlatCard
              title="Rezepthinweise"
              sxContent={{ p: 0.75 }}
              sx={{
                paddingTop: "5px",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              <Notes notes={rxNotes} setNotes={updateNote} maxVisibleNotes={3} noteKind="Rezepthinweis" />
            </FlatCard>

            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData?.doctorNumber,
                clinic: currentClinic,
              }}
              showSignatureCheck={false}
              readOnlyView={true}
              sx={{ paddingTop: "5px", marginBottom: "8px", textAlign: "left" }}
            />
            {rxFormData && (
              <FlatCard 
                title="Validierung" 
                sxContent={{ 
                  p: 1, 
                  overflow: "hidden",
                  "&:last-child": { 
                    paddingBottom: 1 
                  }
                }}
              >
                <Box 
                  sx={{ 
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    margin: 0,
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  <ValidationCard
                    patientData={patientValues}
                    selectedRx={formData}
                    form={formType.SHORT}
                  />
                </Box>
              </FlatCard>
            )}
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", alignItems: "flex-start", border: "0px solid red", borderColor: "transparent" }}
        >
          {" "}
          {/* transparent border color is a hack because i couldnt figure out any way to add top padding*/}
          <ScalableComponentSimple occupiedHeight={readOnlyView ? 0.7 : 1}>
            <Container
              disableGutters
              sx={{ border: "0px solid yellow" }}
              className={classes.container}
              ref={componentRef}
            >
              {/* Small box */}
              <form onSubmit={handleGenericSubmit} className={classes.form}>
                <Grid item sx={{ border: "0px solid green" }}>
                  <Grid container sx={{ width: "100%", height: "230px" }}>
                    {/* LEFT COLUMN */}
                    <Grid
                      item
                      sx={{
                        width: "34px",
                        borderRight: "1px solid #c62828",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <Grid item xs={1}>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -15,
                                top: -20,
                                "&:not(.Mui-checked)": {
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 },
                              }}
                              name="isCopaymentExempted"
                              value={formData?.isCopaymentExempted === true}
                              onChange={() =>
                                setFormData((prev: any) => ({ ...prev, isCopaymentExempted: true }))
                              }
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 10,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Zuzah- lungs- frei
                              </span>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -15,
                                top: 5,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },

                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isCopaymentExempted"
                              value={formData?.isCopaymentExempted === false}
                              onChange={() =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  isCopaymentExempted: false,
                                }))
                              }
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 35,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Zuzah- lungs- pflicht
                              </span>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -15,
                                top: 30,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isAccident"
                              value={formData?.isAccident}
                              onChange={handleInputChange}
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 60,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Unfall- folgen
                              </span>
                            </div>
                          </div>
                        </Grid>

                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -15,
                                top: 55,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isBVG"
                              value={formData?.isBVG}
                              onChange={handleInputChange}
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 80,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                BVG
                              </span>
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/*  EXISTING PATIENT BOX */}
                    <Grid item xs={6}>
                      <div className={classes.box}>
                        {" "}
                        {/* <Grid container spacing={2}> */}{" "}
                        <Grid item xs={12} >
                          {" "}
                          <Typography className={classes.typography} sx={{ mb: 2 }}>
                            {" "}
                            Krankenkasse bzw. Kostenträger{" "}
                          </Typography>{" "}
                          <Controls.Input
                            disabled
                            name="Receiptpayer"
                            label=""
                            value={selectedPayer || formData.Receiptpayer}
                            onChange={handleInputChange}
                            style={{ width: "100%" }}
                          />{" "}
                          {/* <Controls.Select disabled={readOnlyView} name="Receiptpayer" label="" id={1} value={selectedPayer || formData.Receiptpayer} onChange={handleInputChange} options={insurer[0]?.map((option: any) => ({ label: option.ik.toString(), value: option.name.toString(), id: option.id.toString(), }))} style={{ width: "100%", marginTop: "0px", marginBottom: "0px", fontSize: "10px", }} /> */}{" "}
                          <Divider
                            sx={{ flexGrow: 1, background: attentionColors.immediateAttention }}
                          />{" "}
                        </Grid>{" "}
                        <Grid item xs={12}>
                          <Typography className={classes.typography} sx={{ mb: 1 }}>
                            Name, Vorname des Versicherten{" "}
                          </Typography>{" "}
                          <Grid container spacing={2} alignItems="center">
                            {" "}
                            <Grid item xs={4}>
                              {" "}
                              <Controls.Input
                                disabled
                                name=""
                                label=""
                                value={patientLastName}
                                onChange={handleInputChange}
                                style={{ width: "100%", margin: "0px" }}
                              />{" "}
                            </Grid>{" "}
                            <Grid item xs={3}>
                              {" "}
                              <Controls.Input
                                disabled
                                name=""
                                label=""
                                value={patientFirstName}
                                onChange={handleInputChange}
                                style={{ width: "100%", margin: "0px" }}
                              />{" "}
                            </Grid>{" "}
                            <Grid item xs={5}>
                              {" "}
                              <Typography className={classes.typography}>geb . am</Typography>{" "}
                              <Controls.DatePicker
                                name=""
                                label=""
                                value={patientDob}
                                onChange={handleInputChange}
                                disabled
                                variant="standard"
                                style={{
                                  width: "100%",
                                  padding: "0px",
                                  margin: "0px",
                                  fontSize: "10px",
                                  "& .MuiInputBase-input": {
                                    padding: 0,
                                    margin: 0,
                                    paddingRight: "0px",
                                  },
                                }}
                              />{" "}
                            </Grid>{" "}
                          </Grid>{" "}
                          <Divider
                            sx={{ flexGrow: 1, background: attentionColors.immediateAttention }}
                          />{" "}
                        </Grid>{" "}
                        <Grid item xs={12}>
                          {" "}
                          <Grid
                            container
                            spacing={0.5}
                            alignItems="center"
                            sx={{ marginTop: "-8px" }}
                          >
                            {" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>
                                {" "}
                                Kostenträgerkennung{" "}
                              </Typography>{" "}
                              <div style={{ height: "30px" }}>
                                {" "}
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="chipcard"
                                  label=""
                                  value={selectedChipCard || formData?.chipcard}
                                  onChange={handleInputChange}
                                  onKeyDown={handleChipCardDataEntry}
                                  onBlur={handleChipCardDataEntry}
                                  style={{ width: "100%" }}
                                  error={rxerrors.chipcard}
                                />{" "}
                                {/* <Controls.Select disabled={readOnlyView} name="chipcard" label="" id={1} value={selectedChipCard || formData?.chipcard} onChange={handleInputChange} options={insurer[0]?.map((insurer: any) => ({ label: insurer.name.toString(), value: insurer.ik.toString(), }))} style={{ width: "100%", marginTop: "0px", marginBottom: "0px", fontSize: "10px", }} /> */}{" "}
                              </div>
                            </Grid>{" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>
                                {" "}
                                Versicherten-Nr.{" "}
                              </Typography>{" "}
                              <Controls.Input
                                disabled={readOnlyView}
                                name="insureeNumber"
                                label=""
                                value={insureeNumberField.value || ""}
                                onChange={handleInputChange}
                                style={{ width: "100%", margin: "0px" }}
                                error={getKvnrError(insureeNumberField.value)}
                              />{" "}
                            </Grid>{" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>Status</Typography>{" "}
                              <Controls.Select
                                disabled={readOnlyView}
                                name="insuranceStatus"
                                label=""
                                value={insuranceStatusField.value || ""}
                                onChange={handleInputChange}
                                options={[
                                  { label: "10000", value: "10000" },
                                  { label: "30000", value: "30000" },
                                  { label: "50000", value: "50000" },
                                ]}
                                style={{
                                  width: "100%",
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  fontSize: "10px",
                                }}
                              />{" "}
                            </Grid>{" "}
                          </Grid>{" "}
                          <Divider
                            sx={{ flexGrow: 1, background: attentionColors.immediateAttention }}
                          />{" "}
                        </Grid>{" "}
                        <Grid item xs={12}>
                          {" "}
                          <Grid container spacing={2} alignItems="center">
                            {" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>
                                {" "}
                                Betriebsstätten-Nr.{" "}
                              </Typography>{" "}
                              <Controls.Input
                                disabled={readOnlyView}
                                name="practiceNumber"
                                label=""
                                value={formData?.practiceNumber}
                                onChange={handleInputChange}
                                onBlur={handlePracticeNumberInteraction}
                                onKeyDown={handlePracticeNumberInteraction}
                                style={{ width: "100%", margin: "0px" }}
                              />{" "}
                            </Grid>{" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>Arzt-Nr.</Typography>{" "}
                              <Controls.Input
                                disabled={readOnlyView}
                                name="doctorNumber"
                                label=""
                                value={
                                  formData?.doctorNumber || ""
                                }
                                onChange={handleInputChange}
                                style={{ width: "100%", margin: "0px" }}
                                onBlur={handleGetDoctorById}
                                onKeyDown={handleGetDoctorById}
                              />{" "}
                            </Grid>{" "}
                            <Grid item xs={4}>
                              {" "}
                              <Typography className={classes.typography}>Datum</Typography>{" "}
                              <ValidationTooltip
                                validity={validationData?.initialDateVAlidation?.validity}
                                message={validationData?.initialDateVAlidation?.validity}
                                placement="right"
                                sx={{ zIndex: 1300 }}
                              >
                                {" "}
                                <Controls.DatePicker
                                  disabled={readOnlyView}
                                  name="date"
                                  label=""
                                  value={formData?.date}
                                  onChange={handleInputChange}
                                  variant="standard"
                                  style={{
                                    width: "100%",
                                    padding: "0px",
                                    margin: "0px",
                                    fontSize: "10px",
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                      margin: 0,
                                      paddingRight: "0px",
                                    },
                                  }}
                                  formatDate="dd.MM.yy"
                                />{" "}
                              </ValidationTooltip>{" "}
                            </Grid>{" "}
                          </Grid>{" "}
                        </Grid>{" "}
                      </div>
                    </Grid>

                    {/* TITLE */}
                    <Grid item sx={{ flex: 1, pl: 5 }}>
                      <Typography
                        sx={{
                          color: attentionColors.immediateAttention,
                          fontWeight: 700,
                          fontSize: "17px",
                          mt: 1,
                        }}
                      >
                        Zahnärztliche Heilmittelverordnung
                      </Typography>
                    </Grid>
                  </Grid>
                <Grid container spacing={1} sx={{ paddingLeft: "35px !important", mt: 2 }}>
  <Grid item xs={6} sx={{ pl: "50px" }}>
    <div className={classes.subbox}>

      <Typography className={classes.typography} sx={{mb:2}}>
        Verordnung nach Maßgabe des Kataloges
      </Typography>

      {/* Dringlicher Bedarf */}
      <Controls.Checkbox
        disabled={readOnlyView}
        name="urgent"
        value={formData?.urgent}
        onChange={handleInputChange}
        label={
          <Typography className={classes.typography}>
            Dringlicher Behandlungsbedarf innerhalb von 14 Tagen
          </Typography>
        }
      />

      {/* Hausbesuch / Therapiebericht */}
      <Grid
        container
        alignItems="center"
        sx={{ mt: 0.5 }}
      >
        {/* Hausbesuch */}
        <Grid item xs={6}>
          <Typography className={classes.typography}>
            Hausbesuch
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              size="small"
              checked={formData.isHomeVisit === true}
              onChange={handleCheckboxChange}
              disabled={readOnlyView}
              name="visitTypeCheckboxJa"
              value="yes"
            />
            <Typography className={classes.typography}>
              ja
            </Typography>

            <Checkbox
              size="small"
              checked={formData.isHomeVisit === false}
              onChange={handleCheckboxChange}
              disabled={readOnlyView}
              name="visitTypeCheckboxNein"
              value="no"
            />
            <Typography className={classes.typography}>
              nein
            </Typography>
          </Box>
        </Grid>

        {/* Therapiebericht */}
        <Grid item xs={6}>
          <Typography className={classes.typography}>
            Therapiebericht
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Controls.Checkbox
              disabled={readOnlyView}
              name="therapyReport"
              value={formData.therapyReport}
              onChange={handleInputChange}
            />

            <Typography className={classes.typography}>
              ja
            </Typography>
          </Box>
        </Grid>
      </Grid>

    </div>
  </Grid>
</Grid>

                  <Grid container spacing={1} sx={{ paddingLeft: "50px !important",paddingTop: "30px !important" }}>
                     <Typography
                        sx={{
                          color: attentionColors.immediateAttention,
                          fontWeight: 700,
                          fontSize: "18px",
                          mt: 1,
                        }}
                      >
                      Heilmittel nach Maßgabe des Kataloges
                    </Typography>
                    <Grid item xs={12} spacing={0.5}>
                      <Grid container spacing={1}>
                        {/* ======================= HEILMITTEL ======================= */}

                        <Grid
                          container
                          spacing={0}
                          sx={{
                            border: "1px solid #c62828",
                            bgcolor: "#fff",
                          }}
                        >
                          {/* ===================================================== */}
                          {/* LEFT */}
                          {/* ===================================================== */}

                          <Grid
                            item
                            xs={7}
                            sx={{
                              borderRight: "1px solid #c62828",
                            }}
                          >
                            <Grid container spacing={0}>
                              {/* ---------------- Physiotherapie ---------------- */}

                              <Grid
                                item
                                xs={5}
                                sx={{
                                  
                                  p: 0.6,
                                }}
                              >
                                <Typography className={classes.sectionTitle}>
                                  Physiotherapie
                                </Typography>

                                <Typography
                                  className={classes.smallText}
                                  sx={{ fontWeight: "bold", mb: 0.3 }}
                                >
                                  Vorrangige Heilmittel:
                                </Typography>

                                {/* KG */}

                                {/* KG */}
                                <Box className={classes.checkboxLine}>
                                  <Checkbox
                                    size="small"
                                    sx={{ p: 0 }}
                                    checked={isTherapySelected("KG")}
                                    onChange={(e) => handleTherapyCheckbox("KG", e.target.checked)}
                                    disabled={readOnlyView}
                                  />
                                  <Typography className={classes.smallText}>KG</Typography>
                                </Box>

                                {/* KG-ZNS-Kinder */}
                                <Box className={classes.checkboxLine}>
                                  <Checkbox
                                    size="small"
                                    sx={{ p: 0 }}
                                    checked={isTherapySelected("KG-ZNS-Kinder")}
                                    onChange={(e) => handleTherapyCheckbox("KG-ZNS-Kinder", e.target.checked)}
                                    disabled={readOnlyView}
                                  />
                                  <Typography className={classes.smallText}>
                                    KG-ZNS-Kinder
                                  </Typography>
                                </Box>

                                <Box sx={{ pl: 3 }}>
                                  {["KG-ZNS-Kinder Bobath", "KG-ZNS-Kinder Vojta"].map((term) => (
                                    <Box className={classes.checkboxLine} key={term}>
                                      <Checkbox
                                        size="small"
                                        sx={{ p: 0 }}
                                        checked={isTherapySelected(term)}
                                        onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                        disabled={readOnlyView}
                                      />
                                      <Typography className={classes.smallText}>{term.replace("KG-ZNS-Kinder ", "")}</Typography>
                                    </Box>
                                  ))}
                                </Box>

                                {/* KG-ZNS */}
                                <Box className={classes.checkboxLine}>
                                  <Checkbox
                                    size="small"
                                    sx={{ p: 0 }}
                                    checked={isTherapySelected("KG-ZNS")}
                                    onChange={(e) => handleTherapyCheckbox("KG-ZNS", e.target.checked)}
                                    disabled={readOnlyView}
                                  />
                                  <Typography className={classes.smallText}>KG-ZNS</Typography>
                                </Box>

                                <Box sx={{ pl: 3 }}>
                                  {["KG-ZNS Bobath", "KG-ZNS Vojta", "KG-ZNS PNF"].map((term) => (
                                    <Box className={classes.checkboxLine} key={term}>
                                      <Checkbox
                                        size="small"
                                        sx={{ p: 0 }}
                                        checked={isTherapySelected(term)}
                                        onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                        disabled={readOnlyView}
                                      />
                                      <Typography className={classes.subText}>{term.replace("KG-ZNS ", "")}</Typography>
                                    </Box>
                                  ))}
                                </Box>

                                {["MT", "MLD 30", "MLD 45"].map((term) => (
                                  <Box className={classes.checkboxLine} key={term}>
                                    <Checkbox
                                      size="small"
                                      sx={{ p: 0 }}
                                      checked={isTherapySelected(term)}
                                      onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                      disabled={readOnlyView}
                                    />
                                    <Typography className={classes.smallText}>{term}</Typography>
                                  </Box>
                                ))}
                              </Grid>

                              {/* ---------------- Ergänzende Heilmittel ---------------- */}

                              <Grid item xs={7} sx={{ p: 0.6 }}>
                                <Typography className={classes.sectionTitle}>
                                  Ergänzende Heilmittel
                                </Typography>

                                <Typography
                                  className={classes.smallText}
                                  sx={{ fontWeight: "bold", mb: 0.3 }}
                                >
                                  Ergänzende Heilmittel:
                                </Typography>

                                <Grid container spacing={0.5}>
                                  <Grid item xs={4}>
                                    {["Kälte", "Wärme"].map((term) => (
                                      <Box className={classes.checkboxLine} key={term}>
                                        <Checkbox
                                          size="small"
                                          sx={{ p: 0 }}
                                          checked={isTherapySelected(term)}
                                          onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                          disabled={readOnlyView}
                                        />
                                        <Typography className={classes.smallText}>{term}</Typography>
                                      </Box>
                                    ))}
                                  </Grid>
                                  <Grid item xs={8}>
                                    {[ "Elektrostimulation","Elektrotherapie"].map((term) => (
                                      <Box className={classes.checkboxLine} key={term}>
                                        <Checkbox
                                          size="small"
                                          sx={{ p: 0 }}
                                          checked={isTherapySelected(term)}
                                          onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                          disabled={readOnlyView}
                                        />
                                        <Typography className={classes.smallText}>{term}</Typography>
                                      </Box>
                                    ))}
                                  </Grid>
                                </Grid>
                                <Box sx={{ pl: 3 }}>
                                  {["Heißluft", "Heiße Rolle", "Ultraschall", "Packungen"].map(
                                    (term) => (
                                      <Box className={classes.checkboxLine} key={term}>
                                        <Checkbox
                                          size="small"
                                          sx={{ p: 0 }}
                                          checked={isTherapySelected(term)}
                                          onChange={(e) => handleTherapyCheckbox(term, e.target.checked)}
                                          disabled={readOnlyView}
                                        />
                                        <Typography className={classes.subText}>{term}</Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>

                                <Typography
                                  className={classes.smallText}
                                  sx={{
                                    mt: 0.6,
                                    mb: 0.3,
                                    fontWeight: "bold",
                                  }}
                                >
                                  Ggf. Spezifizierung
                                </Typography>

                               

                                <TextField
                                  name="additionalNotes2"
                                  variant="standard"
                                  fullWidth
                                  multiline
                                  maxRows={3}
                                  value={formData?.additionalNotes2}
                                  onChange={handleInputChange}
                                  disabled={readOnlyView}
                                  InputProps={{ disableUnderline: true }}
                                  sx={{
                                    borderBottom: "1px solid #c62828",
                                    mb: 1,
                                    "& input": {
                                      p: "2px",
                                      fontSize: "1rem",
                                    },
                                  }}
                                />

                                <Box className={classes.checkboxLine}>
                                  <Checkbox
                                    size="small"
                                    sx={{ p: 0 }}
                                    checked={isTherapySelected("Übungsbehandlung")}
                                    onChange={(e) => handleTherapyCheckbox("Übungsbehandlung", e.target.checked)}
                                    disabled={readOnlyView}
                                  />
                                  <Typography className={classes.smallText}>
                                    Übungsbehandlung
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* ===================================================== */}
                          {/* CENTER */}
                          {/* ===================================================== */}

                          <Grid
                            item
                            xs={3}
                            sx={{
                              borderRight: "1px solid #c62828",
                              p: 0.6,
                            }}
                          >
                            <Typography className={classes.sectionTitle}>
                              Sprech- und Sprachtherapie
                            </Typography>

                            <Typography className={classes.sectionTitle}>
                              oder Schlucktherapie
                            </Typography>

                            <Typography
                              className={classes.smallText}
                              sx={{
                                textAlign: "center",
                                fontWeight: "bold",
                                mb: 0.4,
                              }}
                            >
                              Therapiedauer
                            </Typography>

                            {["30 min.", "45 min.", "60 min."].map((x) => (
                              <Box className={classes.checkboxLine} key={x}>
                                <Checkbox size="small" sx={{ p: 0 }} />
                                <Typography className={classes.smallText}>{x}____</Typography>
                              </Box>
                            ))}
                          </Grid>

                          {/* ===================================================== */}
                          {/* RIGHT */}
                          {/* ===================================================== */}

                          <Grid item xs={2}>
                            {/* FIRST */}

                            <Box
                              sx={{
                                p: 0.6,
                                borderBottom: "1px solid #c62828",
                              }}
                            >
                              <Typography className={classes.sectionTitle}>
                                Anzahl pro Woche
                              </Typography>

                              {/* <Box sx={{ display: "flex", gap: 0.6, mt: 0.3 }}>
        {[1, 2, 3].map((v) => (
          <Box className={classes.checkboxLine} key={v}>
            <Checkbox size="small" sx={{ p: 0 }} />
            <Typography className={classes.smallText}>
              {v}x
            </Typography>
          </Box>
        ))}
      </Box> */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  mt: 0.3,
                                }}
                              >
                                {[1, 2, 3].map((v, index) => (
                                  <React.Fragment key={v}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        minWidth: 18,
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={parseFrequencyValues(mainTherapyFrequency).has(v)}
                                        onChange={(e) => {
                                          setUnsavedChanges(true);
                                          setMainTherapyFrequency((prev) =>
                                            toggleFrequencyValue(prev, v, e.target.checked)
                                          );
                                        }}
                                        disabled={readOnlyView}
                                        sx={{
                                          p: 0,
                                          "& .MuiSvgIcon-root": {
                                            fontSize: 15,
                                          },
                                        }}
                                      />

                                      <Typography
                                        className={classes.smallText}
                                        sx={{
                                          fontSize: "0.65rem !important",
                                          lineHeight: 1,
                                          mt: 0.1,
                                        }}
                                      >
                                        {v}x
                                      </Typography>
                                    </Box>

                                    {index < 2 && (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          borderTop: "1px dashed #c62828",
                                          mt: "7px",
                                          mx: 0.2,
                                        }}
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                              </Box>

                              <Typography
                               
                                sx={{
                                  fontWeight: "bold",
                                   fontSize: "0.7rem",
                                  color:"#c62828",
                                 
                                }}
                              >
                                Verordnungsmenge
                              </Typography>

                              <TextField
                                size="small"
                                value={mainTherapyAmount}
                                onChange={(e) => {
                                  setUnsavedChanges(true);
                                  setMainTherapyAmount(e.target.value);
                                }}
                                disabled={readOnlyView}
                                sx={{
                                  width: 42,
                                  "& input": {
                                    p: "3px",
                                    textAlign: "center",
                                  },
                                }}
                              />
                            </Box>

                            {/* SECOND */}

                            <Box sx={{ p: 0.6 }}>
                              <Typography
                             
                                sx={{
                                  fontStyle: "italic",
                                 fontSize: "0.5rem",
                                  color:"#c62828",
                                }}
                              >
                                ggf. ergänzendes Heilmittel
                              </Typography>

                              <Typography className={classes.sectionTitle}>
                                Anzahl pro Woche
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  mt: 0.3,
                                }}
                              >
                                {[1, 2, 3].map((v, index) => (
                                  <React.Fragment key={v}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        minWidth: 18,
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={parseFrequencyValues(suppTherapyFrequency).has(v)}
                                        onChange={(e) => {
                                          setUnsavedChanges(true);
                                          setSuppTherapyFrequency((prev) =>
                                            toggleFrequencyValue(prev, v, e.target.checked)
                                          );
                                        }}
                                        disabled={readOnlyView}
                                        sx={{
                                          p: 0,
                                          "& .MuiSvgIcon-root": {
                                            fontSize: 15,
                                          },
                                        }}
                                      />

                                      <Typography
                                        className={classes.smallText}
                                        sx={{
                                          fontSize: "0.65rem !important",
                                          lineHeight: 1,
                                          mt: 0.1,
                                        }}
                                      >
                                        {v}x
                                      </Typography>
                                    </Box>

                                    {index < 2 && (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          borderTop: "1px dashed #c62828",
                                          mt: "7px",
                                          mx: 0.2,
                                        }}
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                              </Box>

                              <Typography
                                
                                sx={{
                                  fontWeight: "bold",
                                   fontSize: "0.7rem",
                                  color:"#c62828",
                                
                                }}
                              >
                                Verordnungsmenge
                              </Typography>

                              <TextField
                                size="small"
                                value={suppTherapyAmount}
                                onChange={(e) => {
                                  setUnsavedChanges(true);
                                  setSuppTherapyAmount(e.target.value);
                                }}
                                disabled={readOnlyView}
                                sx={{
                                  width: 42,
                                  "& input": {
                                    p: "3px",
                                    textAlign: "center",
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                        {/* Second Column: Symptoms and Diagnosis Text */}

                        <Table
  className={classes.sectionBox}
  sx={{
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",

    "& td, & th": {
      border: "1px solid #c62828",
      padding: "2px 3px",
      verticalAlign: "top",
    },

    "& .MuiTypography-root": {
      fontSize: classes.smallText,
      lineHeight: 1.1,

    },
  }}
>
  <TableBody>
    {/* ------------------------- FIRST ROW ------------------------- */}
    <TableRow sx={{ height: 42 }}>
      <TableCell sx={{ width: "27%" }}>
        <Typography className={classes.subText} sx={{ fontWeight: 700, mb: 0.3  }}>
          Indikationsschlüssel
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: "1px",
            alignItems: "center",
            mt: 0.3,
          }}
        >
          {inputSymptoms?.map((value, index) => (
            <input
                                         key={index}
                                         type="text"
                                         value={value}
                                         onChange={(event) => handleInputChange(event, index)}
                                         className={classes.compartment}
                                         maxLength={1}
                                         name="inputSymptoms"
                                         disabled={readOnlyView}
                                         ref={(el) => (inputRefs.current[index] = el)}
                                         onKeyDown={(e) => {
                                           if (e.key === "ArrowRight") {
                                             // Move focus to the next input on ArrowRight
                                             if (index < inputSymptoms.length - 1) {
                                               const nextInput = inputRefs.current[index + 1];
                                               if (nextInput) {
                                                 nextInput.focus();
                                               }
                                             }
                                           } else if (e.key === "ArrowLeft") {
                                             // Move focus to the previous input on ArrowLeft
                                             if (index > 0) {
                                               const prevInput = inputRefs.current[index - 1];
                                               if (prevInput) {
                                                 prevInput.focus();
                                               }
                                             }
                                           } else if (e.key === "Backspace") {
                                             if (!e.currentTarget.value) {
                                               // If the current input is empty and backspace is pressed
                                               if (index > 0) {
                                                 const prevInput = inputRefs.current[index - 1];
                                                 if (prevInput) {
                                                   prevInput.focus();
                                                   // Optionally clear the previous input's value
                                                   handleInputChange(
                                                     {
                                                       target: {
                                                         name: "inputSymptoms",
                                                         value: "",
                                                       },
                                                     } as React.ChangeEvent<HTMLInputElement>,
                                                     index - 1
                                                   );
                                                 }
                                               }
                                               e.preventDefault(); // Prevent the default backspace behavior
                                             }
                                           }
                                         }}
                                       />
          ))}
        </Box>
      </TableCell>

      <TableCell sx={{ width: "73%" }}>
        <Typography sx={{ mb: 0.3 }} className={classes.smallText}>
          Diagnose mit Leitsymptomatik, ggf. wesentliche Befunde, ggf.
          Spezifizierung
        </Typography>

        <Controls.TextArea
          name="symptoms"
          disabled={readOnlyView}
          rows={2}
          value={formData.symptoms || ""}
          onChange={handleInputChange}
          style={{
            width: "100%",
            border: "none",
            fontSize:"14px",
            padding: "2px",
            resize: "none",
          }}
        />
      </TableCell>
    </TableRow>

    {/* ---------------------- ICD ROWS ----------------------- */}
    {icdCodeEntries.map((icdEntry, index) => (
      <TableRow key={index} sx={{ height: 34 }}>
        <TableCell>
          <Typography className={classes.subText}  sx={{ fontWeight: 700, mb: 0.3 }}>
            ICD-10 Code
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1px",
            }}
          >
            
            {Array.from({ length: 5 }).map((_, compartmentIndex) => (
              <React.Fragment key={compartmentIndex}>
                <input
                                                   name={`icdCodeEntries-${index}-${compartmentIndex}`}
                                                   type="text"
                                                   className={classes.compartment}
                                                   value={
                                                     compartmentIndex === 3 &&
                                                     icdEntry.userText[compartmentIndex] === "."
                                                       ? ""
                                                       : icdEntry.userText[compartmentIndex] || ""
                                                   }
                                                   onChange={(event) =>
                                                     handleICDInputChange(event, index, compartmentIndex)
                                                   }
                                                   disabled={readOnlyView}
                                                   maxLength={1}
                                                   style={{ marginRight: compartmentIndex === 2 ? "0px" : "2px" }}
                                                   ref={(input) =>
                                                     (inputRefs.current[`${index}-${compartmentIndex}`] = input)
                                                   }
                                                   onKeyDown={(e) => {
                                                     if (e.key === "Enter") {
                                                       handleICDData(icdEntry, index);
                                                       // handleICDCodeEntrySubmit(icdEntry, index);
                                                     } else if (e.key === "Backspace") {
                                                       if (!e.currentTarget.value) {
                                                         // If the current input is empty and backspace is pressed
                                                         if (compartmentIndex > 0) {
                                                           const prevCompartmentIndex = compartmentIndex - 1;
                                                           const prevInput =
                                                             inputRefs.current[`${index}-${prevCompartmentIndex}`];
               
                                                           // Clear the current cell data
                                                           handleICDInputChange(
                                                             {
                                                               target: {
                                                                 name: `icdCodeEntries-${index}-${compartmentIndex}`,
                                                                 value: "",
                                                               },
                                                             } as React.ChangeEvent<HTMLInputElement>,
                                                             index,
                                                             compartmentIndex
                                                           );
               
                                                           // Focus on the previous input
                                                           if (prevInput) {
                                                             prevInput.focus();
               
                                                             // Clear the previous input's value if necessary
                                                             handleICDInputChange(
                                                               {
                                                                 target: {
                                                                   name: `icdCodeEntries-${index}-${prevCompartmentIndex}`,
                                                                   value: "",
                                                                 },
                                                               } as React.ChangeEvent<HTMLInputElement>,
                                                               index,
                                                               prevCompartmentIndex
                                                             );
                                                           }
                                                         }
                                                         e.preventDefault(); // Prevent the default backspace behavior
                                                       }
                                                     } else if (e.key === "ArrowRight") {
                                                       // Move focus to the next cell on ArrowRight
                                                       if (compartmentIndex < 4) {
                                                         const nextCompartmentIndex = compartmentIndex + 1;
                                                         const nextInput =
                                                           inputRefs.current[`${index}-${nextCompartmentIndex}`];
                                                         if (nextInput) {
                                                           nextInput.focus();
                                                         }
                                                       }
                                                     } else if (e.key === "ArrowLeft") {
                                                       // Move focus to the previous cell on ArrowLeft
                                                       if (compartmentIndex > 0) {
                                                         const prevCompartmentIndex = compartmentIndex - 1;
                                                         const prevInput =
                                                           inputRefs.current[`${index}-${prevCompartmentIndex}`];
                                                         if (prevInput) {
                                                           prevInput.focus();
                                                         }
                                                       }
                                                     }
                                                   }}
                                                 />

                {compartmentIndex === 2 && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "11px",
                      px: 0.3,
                    }}
                  >
                    .
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        </TableCell>

        <TableCell>
          <Controls.TextArea
            rows={1}
            disabled={readOnlyView}
            name={`diagnosisText-${index}`}
            value={lines[index] || ""}
            onChange={(e:any) => handleInputChange(e, index)}
            style={{
              width: "100%",
              border: "none",
              fontSize: "14px",
              padding: "2px",
              resize: "none",
            }}
          />
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
</Table>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    sx={{ paddingLeft: "40px !important", position: "relative" }}
                  >
                    {/* First Column */}
                    <Grid item xs={12}>
                      <div className={classes.gridBox} style={{ position: "relative" }}>
                        <Typography className={classes.smallText}>
                          Weitere Hinweise (ggf. Angaben/Begründung zum langfristigen
                          Heilmittelbedarf, Angaben zur Biankoverdnung, etc.)
                        </Typography>

                        <Controls.TextArea
                          disabled={readOnlyView}
                          name="additionalNotes"
                          rows={9}
                          placeholder=""
                          value={formData?.additionalNotes}
                          onChange={handleInputChange}
                          style={{ flex: 1, height: "150px", width: "100%" }}
                        />

                        {/* Doctor Info positioned in the top right corner */}
                        <div
                          style={{ position: "absolute", top: 70, right: 0, width: "40%" }}
                          className={classes.gridBox}
                        >
                          <Controls.TextArea
                            label={
                              <Typography className={classes.smallText}>
                                Verordnende Ärztin/Verordnender Arzt
                              </Typography>
                            }
                            disabled
                            name="doctorData"
                            rows={7}
                            placeholder=""
                            value={
                              `${currentDoctor?.contactInfo?.salutation || ""} ${
                                currentDoctor?.contactInfo?.firstNames || ""
                              } ${currentDoctor?.contactInfo?.lastName || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine1 || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine2 || ""}\n` +
                              `${currentClinic?.contactInfo?.zip || ""}\n` +
                              `Tel: ${currentClinic?.contactInfo?.phone1 || ""}\n` +
                              `BSNR: ${currentClinic?.id || ""}`
                            }
                            onChange={handleInputChange}
                            style={{ flex: 1, width: "100%" }}
                          />
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Typography className={classes.smallText}>
                        IK des Leitungserbringers
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          background: "white",
                          width: "100px",
                          height: "20px",
                        }}
                      ></Box>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
              <Modal
                open={loading}
                aria-labelledby="loading-modal-title"
                aria-describedby="loading-modal-description"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <CircularProgress size={60} />
                </div>
              </Modal>
            </Container>
          </ScalableComponentSimple>
        </Grid>
        {!readOnlyView && (
          <Grid item xs={12} md={3} sx={{ border: "0px dotted black" }}>
            <FlatCard title="Validierung">
              <ValidationCard
                patientData={patientValues}
                selectedRx={
                  // patientValues?.rxs?.filter((rx: any) => rx.rxNumber === rxFormData?.rxNumber)[0]
                  formData
                }
                form={formType.LONG}
              />
            </FlatCard>
          </Grid>
        )}
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}
      </Grid>
    );

    // if (!selectedRxId || !entryType) {
    //   return (
    //     <Box p={2}>
    //       <span>Kein Rezept ausgewählt</span>
    //     </Box>
    //   );
    // }

    if (isRxLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (rxError) {
      return (
        <Box p={2}>
          <span>Fehler beim Laden des Rezepts für {selectedRxId}</span>
        </Box>
      );
    }
    return !readOnlyView ? (
      <Dialog
        open={openRx as boolean}
        onClose={handleCloseRxDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            left: position === "right-third" ? `${window.innerWidth * 0.33}px` : "auto",
            top: position === "right-third" ? "50%" : "auto",
            transform: position === "right-third" ? "translateY(-50%)" : "none",
            width: position === "right-third" ? `${window.innerWidth * 0.67}px` : "auto",
            height: position === "right-third" ? "100vh" : "auto",
            maxWidth: position === "right-third" ? "none" : "xl",
            margin: position === "right-third" ? "0" : "auto",
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          Zahnarzt Dialog
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPosition(position === "default" ? "right-third" : "default")}
            sx={{ minWidth: "40px", width: "40px", height: "40px" }}
          >
            1
          </Button>
        </DialogTitle>
        <DialogContent>{FormContent}</DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseRxDialog}>
            Abbrechen
          </Button>
          <Button variant="text" onClick={() => setDocumentViewerOpen(!documentViewerOpen)}>
            Dokumentviewer
          </Button>
          <Button
            onClick={handleGenericSubmit}
            variant="contained"
            disabled={Object.values(rxerrors).some((error) => error !== "")}
          >
            Speichern
          </Button>
        </DialogActions>
        <VerordnungDialog
          openAddEditDialog={isClinicModalOpen}
          handleCloseAddEditDialog={() => { setIsClinicModalOpen(false); setIsClinicModalEditMode(false); }}
          clinicId={currentClinicId}
          setCreationResponse={setCreationResponse}
          doctorId={currentDoctorId}
          currentClinic={currentClinic}
          currentDoctor={currentDoctor}
          initialDoctorName={autocompleteSearchTerm}
          isEditMode={isClinicModalEditMode}
        />

        {/* SimpleDocumentViewer Komponente */}
        <SimpleDocumentViewer
          open={documentViewerOpen}
          onClose={() => setDocumentViewerOpen(false)}
          position={position}
          patientId={patientValues?.id}
        />
      </Dialog>
    ) : (
      FormContent
    );
  }
);

export default Zahnarzt;
